import * as Phaser from 'phaser';
import { rgb24, type Rgba } from '../../sim/color.ts';
import { PIXELS_PER_METER } from '../../sim/constants.ts';
import { angleDelta, clamp, length, sub, toAngle } from '../../sim/math.ts';
import { formatScaleLabel, gridCellMeters } from '../../sim/grid.ts';
import {
  beginCheckpointDraft,
  cancelCheckpointDraft,
  cancelDraft,
  canCloseDraft,
  canFinishRoute,
  canRedo,
  canUndo,
  clearIslandEdgeBend,
  clearSelection,
  clearShallowWaterEdgeBend,
  clearParkAreaEdgeBend,
  clearStartAreaEdgeBend,
  abandonLast,
  applyMapImportFix,
  cloneMapDocument,
  cloneAimChrome,
  clampToBounds,
  closestPointOnSegment,
  closeDraft,
  copySelection,
  createEmptyMap,
  createHistory,
  createSession,
  cutSelection,
  deleteSelection,
  duplicateSelection,
  finishCheckpointDraft,
  findCheckpoint,
  findIsland,
  findIslandEdgeAt,
  importMapDocument,
  importSuccessMessage,
  insertRingVertex,
  insertRouteVertex,
  findShallowWater,
  findShallowWaterEdgeAt,
  findRoute,
  findRouteAt,
  findRouteSegmentAt,
  findParkAreaEdgeAt,
  findStartAreaEdgeAt,
  finishRoute,
  formatRouteMetres,
  nextRouteColor,
  removeRoute,
  routeLengthM,
  routeOutline,
  UNSELECTED_ROUTE_ALPHA,
  hasRef,
  isNear,
  moveFlag,
  moveIslandBend,
  moveSelection,
  rotateSelection,
  scaleSelection,
  GIZMO_CORNERS,
  gizmoCornerPoint,
  gizmoOppositeCorner,
  gizmoRotateHandlePoint,
  gizmoShowsRotate,
  gizmoShowsScale,
  ringSculptingEnabled,
  selectionGizmoMode,
  withoutRingSub,
  type GizmoCorner,
  type SelectionGizmoMode,
  moveShallowWaterBend,
  moveShallowWaterVertex,
  moveParkAreaBend,
  moveParkAreaVertex,
  moveStartAreaBend,
  moveStartAreaVertex,
  moveRouteVertex,
  moveVertex,
  normalizeRect,
  pasteFromClipboard,
  pushUndo,
  reconcileSelection,
  rectArea,
  serializeMap,
  redo,
  refAt,
  refBounds,
  resolveColor,
  selectAll,
  selectOnly,
  selectInRect,
  selectionBounds,
  selectCheckpoint,
  selectedCheckpointPoles,
  selectIsland,
  selectParkArea,
  selectStartArea,
  selectWater,
  selectedEdgeIndex,
  selectedFlag,
  selectedIds,
  soleRef,
  setCheckpointGoal,
  setCheckpointOrder,
  setDraftMaterial,
  setDraftProp,
  setDraftTerraceKind,
  setDraftTerraceLevels,
  setDraftTerracePeakM,
  setDraftTerraceRadius,
  setDraftTerraceColor,
  setDraftTerraceColorVariation,
  setDraftTerraceJitter,
  setDraftTerraceElevRand,
  setDraftTerraceRadiusRand,
  setGateWidth,
  POLE_HEIGHT_WORLD,
  POLE_RADIUS_WORLD,
  poleWorldScale,
  SNAP_GRID,
  SNAP_SIZE_STEP,
  labelFromFilename,
  mapDisplayName,
  sanitizeMapFilename,
  snapAngleRad,
  toggleSelection,
  setIslandColor,
  setIslandCornerRadius,
  setIslandSkirtM,
  plantIslandProp,
  removeIslandDecoration,
  findDecorationAt,
  findIslandAtPlateau,
  waterlineFromPlateau,
  worldElevation,
  scatterIsland,
  clearIslandDecorations,
  setLaps,
  setMapSize,
  setSnapToGrid,
  snapDelta,
  snapPoint,
  snapValue,
  updateEnvironment,
  updateLook,
  setDraftDepth,
  setShallowWaterColor,
  setShallowWaterCornerRadius,
  setShallowWaterDepth,
  setShallowWaterMaterial,
  setRouteColor,
  setParkAreaColor,
  setParkAreaCornerRadius,
  setStartAreaColor,
  setStartAreaCornerRadius,
  setTool,
  toggleCheckpointFlag,
  tryPlaceDrawPoint,
  tryPlaceRoutePoint,
  trySelectAt,
  undo,
  validateMap,
  clearRockEdgeBend,
  findRock,
  findRockEdgeAt,
  moveRockBend,
  moveRockVertex,
  selectRock,
  selectRoute,
  setIslandElevation,
  setRockColor,
  setRockCornerRadius,
  setRockElevation,
  mapMetaEquals,
  sanitizeMapMeta,
  setMapMeta,
  setMapName,
  suggestMapFilename,
  type Checkpoint,
  type CheckpointFlag,
  type EditHistory,
  type EditorSession,
  type EditorTool,
  type MapDocument,
  type MapMeta,
  type ObjectRef,
  type Ring,
  type MapLook,
  type AimChrome,
  type SunSettings,
  type Rect,
  type Vec2,
  type ViewRect,
  type WaterMaterial,
} from '../../sim/map/index.ts';
import {
  appendValidationLog,
  type EditorLogBadge,
  type EditorLogEntry,
  type EditorLogFilter,
} from '../../ui/editor/log.ts';
import { EditorMinimap } from '../editor/minimap.ts';
import {
  REFERENCE_IMAGE_TEXTURE_KEY,
  REFERENCE_MIN_SIZE,
  clearReferenceImageSession,
  fitReferenceInBounds,
  getReferenceImageSession,
  hasReferenceImage,
  loadHtmlImage,
  moveReferenceRect,
  patchReferenceImageSession,
  referenceImageFrame,
  scaleReferenceRect,
  setReferenceImageSession,
  type ReferenceImageRect,
  type ReferenceImageSession,
} from '../editor/referenceImage.ts';
import {
  canDeleteRepoMap,
  canWriteRepoMap,
  clearResumableDraft,
  deleteRepoMap,
  exportMapFile,
  pickImageFile,
  pickMapJsonFile,
  loadResumableDraft,
  mapFileDisplayName,
  saveMap,
  saveRepoFileName,
  saveResumableDraft,
  UNTITLED_MAP_NAME,
  type ResumableDraft,
  type SaveMapResult,
} from '../../ui/editor/persist.ts';
import {
  dropRecentMap,
  loadEditorSettings,
  patchEditorSettings,
  recordRecentMap,
} from '../../ui/settingsStore.ts';
import type { RecentMap } from '../../sim/settings/index.ts';
import {
  confirmDialog,
  promptMapFilename,
  promptMapSave,
  reportMapImportFailure,
} from '../../ui/editor/dialog.tsx';
import { promptOpenMap } from '../../ui/editor/openMap.tsx';
import {
  findMapCatalogEntry,
  loadMapCatalog,
} from '../maps/catalog.ts';
import { loadBoatCatalog } from '../boats/catalog.ts';
import {
  boatTestChoices,
  handoffFromEditor,
  resolveTestBoat,
  type TestChoice,
} from '../testPlay.ts';
import type { SceneScale } from '../../ui/sceneScale.tsx';
import { mountSceneScale } from '../../ui/sceneScale.tsx';
import type { EditorToolbar } from '../../ui/editor/toolbar.tsx';
import { mountEditorToolbar } from '../../ui/editor/toolbar.tsx';
import type { EditorValidation } from '../../ui/editor/validation.tsx';
import { mountEditorValidation } from '../../ui/editor/validation.tsx';
import { mountEditorPerf, type EditorPerf } from '../../ui/editor/perfPanel.tsx';
import { FrameCoalescer } from '../frameCoalescer.ts';
import { ScenePerf } from '../perf/scenePerf.ts';
import { EMPTY_PERF_READOUT } from '../../ui/play/perfReadout.ts';
import {
  createFpsClock,
  mountFps,
  pushFps,
  type FpsClock,
  type FpsMeter,
} from '../../ui/fps.tsx';
import { mountOverlay, showOverlay } from '../../ui/overlay.tsx';
import {
  BOAT_GUIDE_BEAM_PX,
  BOAT_GUIDE_LENGTH_PX,
  boatGuideCenter,
  boatGuideHeadingAfterWheel,
} from '../editor/boatGuide.ts';
import { SunLighting } from '../lighting/sunLighting.ts';
import { CameraControl } from '../cameraControl.ts';
import { setEditorCursor } from '../cursors.ts';
import { cursorForEditorTool, hoverCursorForEditorTool } from '../editorCursor.ts';
import { applyMapBackdrop, backdropSignature } from '../mapBackdrop.ts';
import {
  RING_SELECTED_STROKE_PX,
  RING_STROKE_PX,
  drawTerrain,
  paintRing,
  paintWaterlineHints,
  type TerrainGraphics,
  type TerrainLayer,
} from '../mapRender.ts';
import { applyColumnVisibility } from '../columnVisibility.ts';
import { ElevatedBodyGraphicsPool, type TerrainPaintMode } from '../elevatedLayers.ts';
import { RENDER_FLAGS } from '../renderFlags.ts';
import { StaticMesh } from '../terrain/staticMesh.ts';
import { IslandOverlayPool } from '../islandOverlays.ts';
import { CheckpointPolePool, drawCheckpoints, syncCheckpointBadges } from '../checkpointRender.ts';
import { isFloatingPanelOpen } from '../../ui/floatingPanel.tsx';
import { bindOverlayTyping, isTypingInField } from '../../ui/overlayTyping.ts';
import { clearEscapeOwner, setEscapeOwner } from '../../ui/pause/escape.ts';
import { isPauseMenuOpen, mountPauseMenu, type PauseMenu } from '../../ui/pause/menu.tsx';
import { clearSceneSearch } from '../mode.ts';
import {
  EDITOR_DEFAULT_ZOOM,
  EDITOR_MIN_ZOOM,
  GRID_SCREEN_PX,
  MAX_ZOOM,
  VOID_COLOR,
  cameraWorldRect,
  screenToWorld,
} from '../view.ts';
import { setPlayCursor } from '../playCursor.ts';
import { noteZoom } from '../zoomChrome.ts';

const CLOSE_RADIUS_PX = 12;
const HANDLE_RADIUS_PX = 8;
const BEND_HANDLE_RADIUS_PX = 7;
const SELECT_RADIUS_PX = 10;

const DRAFT_STROKE_PX = 2;

/**
 * Floor for the pole hit box at zoom 1. Painted poles follow `checkerWorldScale`, so the handle
 * grows with them when the camera pulls back.
 */
const FLAG_RADIUS_WORLD = 16;

const SELECTED_HIGHLIGHT = 0xf4f7ee;
const DRAFT_STROKE = 0xf0d78c;

const VERTEX_HANDLE_FILL = 0xf4f7ee;
const VERTEX_HANDLE_STROKE = 0x1b2a22;
const BEND_HANDLE_FILL = 0xf0d78c;
const BEND_HANDLE_STROKE = 0x6b5a1f;
const RESIZE_HANDLE_FILL = 0x7fd4ff;
const ROTATE_HANDLE_FILL = 0xffc14a;
/** Yellow rotate dots sit this far outside each corner so they do not overlap the cyan ones. */
const ROTATE_HANDLE_OFFSET_PX = HANDLE_RADIUS_PX * 2 + 4;

const NUDGE_STEP_PX = 1;
const NUDGE_COARSE_PX = 10;
/** Below this, a marquee drag was really just a click on empty water. */
const MIN_MARQUEE_AREA = 16;
const SELECTION_BOX_PAD_PX = 6;

const OVERLAY_DEPTH = 8;
/** Above drafts and handles so the hull stays readable on top of whatever tool is active. */
const BOAT_GUIDE_DEPTH = 9;
const HANDLE_DEPTH = 7;
const FLAG_DEPTH = 6;
const LABEL_DEPTH = 6;
const CHECKPOINT_DEPTH = 4;
/** Props and pole shafts; south / larger world y is in front. Stays under draft (5). */
const PROP_POLE_Y_SORT_SPAN = 0.9;
const CHECKPOINT_WATERLINE_DEPTH = 1.35;
const CHECKPOINT_SUBMERGED_DEPTH = 1;
const DRAFT_DEPTH = 5;
const ROUTE_DEPTH = 4.6;
const ROUTE_STROKE_PX = 2;
const START_AREA_DEPTH = 2.5;
const PARK_AREA_DEPTH = 2.45;
/** Above every column wall so the north-shore dash stays visible on the cliff. */
const WATERLINE_HINT_DEPTH = 6.9;
/** Tracing overlay sits over terrain and under vertex / gizmo handles. */
const REFERENCE_DEPTH = 6.5;
const ROCK_DEPTH = 2.2;
const LAND_DEPTH = 2;
const SKIRT_DEPTH = 1.85;
const WATER_SHEET_DEPTH = 1.25;
/** Shallow beds sit under the water sheet so they read as thinner water, not surface stickers. */
const WATER_DEPTH = 1.1;
const GRID_DEPTH = 0;

const DRAW_TOOLS: ReadonlySet<EditorTool> = new Set([
  'draw-island',
  'draw-rock',
  'draw-terrace',
  'draw-water',
  'edit-start-area',
  'edit-park-area',
]);

const SELECTION_TOOLS: ReadonlySet<EditorTool> = new Set(['select', 'scale', 'rotate']);

function isSelectionTool(tool: EditorTool): boolean {
  return SELECTION_TOOLS.has(tool);
}

function overlayCapturesKeys(): boolean {
  return isTypingInField() || isFloatingPanelOpen() || isPauseMenuOpen();
}

type RingOwnerKind = 'island' | 'rock' | 'water' | 'startArea' | 'parkArea';
type RingOwner = { kind: RingOwnerKind; id: string };
type BendDrag = { owner: RingOwner; edgeIndex: number };

function strokePolygon(
  gfx: Phaser.GameObjects.Graphics,
  points: readonly Vec2[],
  close: boolean,
): void {
  const first = points[0];

  if (!first) {
    return;
  }

  gfx.beginPath();
  gfx.moveTo(first.x, first.y);

  for (let i = 1; i < points.length; i += 1) {
    const point = points[i];

    if (point) {
      gfx.lineTo(point.x, point.y);
    }
  }

  if (close) {
    gfx.closePath();
    gfx.fillPath();
  }

  gfx.strokePath();
}

function isAdditivePointer(pointer: Phaser.Input.Pointer): boolean {
  const event = pointer.event;

  if (!(event instanceof MouseEvent)) {
    return false;
  }

  return event.shiftKey || event.ctrlKey || event.metaKey;
}

function ringCenter(ring: Ring): Vec2 {
  const total = ring.vertices.reduce(
    (acc, vertex) => ({ x: acc.x + vertex.x, y: acc.y + vertex.y }),
    { x: 0, y: 0 },
  );
  const count = ring.vertices.length || 1;
  return { x: total.x / count, y: total.y / count };
}

export class EditorScene extends Phaser.Scene {
  private map!: MapDocument;
  private session!: EditorSession;
  private history: EditHistory = createHistory();
  private fileHandle: FileSystemFileHandle | null = null;
  /** `maps/<name>.json` (DEV). Imports show `importedName` instead so Save cannot overwrite a catalog file. */
  private repoFile: string | null = null;
  private importedName: string | null = null;
  /** Serialized map at last load or successful save. Drafts exist only while the working copy differs. */
  private baselineJson = '';
  /** True while the resume dialog is open, so a second close cannot wipe the draft. */
  private draftOfferPending = false;
  /** The document is mutated in place, so the toolbar needs a counter to see edits. */
  private mapRevision = 0;
  /** Zoom the map layers were last stroked for. A drift inside 8% does not restroke. */
  private mapLayersZoom = EDITOR_DEFAULT_ZOOM;

  private toolbar: EditorToolbar | null = null;
  private pauseMenu: PauseMenu | null = null;
  private leaving = false;
  private scaleMeter: SceneScale | null = null;
  private validation: EditorValidation | null = null;
  private fpsHud: FpsMeter | null = null;
  private fpsClock: FpsClock = createFpsClock();
  /** Render probe behind View → Perf (docs/PERFORMANCE.md). */
  private scenePerf: ScenePerf | null = null;
  private perfPanel: EditorPerf | null = null;
  /**
   * One repaint per frame while a gesture is running.
   *
   * `POST_STEP` is Phaser's last event before rendering starts, and the scene's pointer
   * handlers have already run by then (`Game.step`: PRE_STEP flushes input, `scene.update`
   * dispatches it, POST_STEP, then render). Queuing there paints in the same frame the
   * move arrived in; a `requestAnimationFrame` would land behind the game loop and show a
   * frame late.
   */
  private readonly dragPaint = new FrameCoalescer({
    schedule: (fn) => {
      const once = (): void => fn();
      this.game.events.once(Phaser.Core.Events.POST_STEP, once);
      return () => this.game.events.off(Phaser.Core.Events.POST_STEP, once);
    },
  });

  private gridGraphics!: Phaser.GameObjects.Graphics;
  private waterSheetGraphics!: Phaser.GameObjects.Graphics;
  private waterGraphics!: TerrainLayer;
  private waterChromeGraphics!: TerrainLayer;
  private landGraphics!: Phaser.GameObjects.Graphics;
  private waterlineHintGraphics!: TerrainLayer;
  private skirtGraphics!: TerrainLayer;
  private elevatedLayers: ElevatedBodyGraphicsPool | null = null;
  private islandOverlays: IslandOverlayPool | null = null;
  private columnVisibleKey = '';
  private columnVisibleIds: ReadonlySet<string> | null = null;
  /** Padded world view the last cull ran for; a barely-moved camera skips the recompute. */
  private columnVisibleView: ViewRect | null = null;
  private checkpointPoles: CheckpointPolePool | null = null;
  private rockGraphics!: Phaser.GameObjects.Graphics;
  private startAreaGraphics!: Phaser.GameObjects.Graphics;
  private parkAreaGraphics!: Phaser.GameObjects.Graphics;
  private checkpointSubmergedGraphics!: TerrainLayer;
  private checkpointWaterlineGraphics!: TerrainLayer;
  private checkpointGraphics!: TerrainLayer;
  private routeGraphics!: Phaser.GameObjects.Graphics;
  private draftGraphics!: Phaser.GameObjects.Graphics;
  private overlayGraphics!: Phaser.GameObjects.Graphics;
  private referenceImage: Phaser.GameObjects.Image | null = null;
  private referenceImageAlpha = 0.2;
  private referenceDrag:
    | { kind: 'scale'; origin: Vec2; start: ReferenceImageRect }
    | { kind: 'move'; start: ReferenceImageRect; startPoint: Vec2 }
    | null = null;

  /** Toolbar hook: when on, the same sun-column water lighting as play. */
  private previewLighting = false;
  /** Play-mode 2.5-D island/rock columns. Independent of Preview lighting. */
  private showElevation = false;
  private showGrid = false;
  private showScale = true;
  private showBoat = false;
  private showMinimap = true;
  /** View → Perf. `?perf` forces it on, same as play's Debug → Perf section. */
  private showPerf = false;
  /** Camera signature the grid / sheet were last drawn for. */
  private gridDrawnFor = '';
  /** Gate signature the checkpoint layers were last painted for. */
  private checkpointsDrawnFor = '';
  /** True-size hull. Shown while View → Boat is on, or while B is held. */
  private boatGuide: Phaser.GameObjects.Image | null = null;
  /** Hold B. Not a setting: release leaves the View toggle where it was. */
  private boatHeld = false;
  /** Bow-right is 0°. Wheel while B is held steps this; it survives release. */
  private boatGuideHeading = 0;
  /** Last canvas point under the pointer, in game pixels. Null once the pointer leaves the map. */
  private boatGuideScreen: { x: number; y: number } | null = null;
  private showRoutes = true;
  private hiddenRouteIds = new Set<string>();
  private lighting: SunLighting | null = null;
  private sunEditing = false;
  private lookEditing = false;
  private colorEditing = false;
  private sunBakeFrame: number | null = null;

  private startAreaLabel: Phaser.GameObjects.Text | null = null;
  private parkAreaLabel: Phaser.GameObjects.Text | null = null;
  private checkpointFlagHandles = new Map<
    string,
    { a: Phaser.GameObjects.Zone; b: Phaser.GameObjects.Zone }
  >();
  private checkpointLabels = new Map<string, Phaser.GameObjects.Text>();
  private routeLabels = new Map<string, Phaser.GameObjects.Text>();
  private handles: Array<Phaser.GameObjects.Arc | Phaser.GameObjects.Graphics> = [];
  private minimap: EditorMinimap | null = null;
  /** Set while the pointer is scrubbing the minimap, so a drag keeps steering the camera. */
  private minimapDrag = false;
  private spaceDown = false;
  /** Native pointer that owns a Space / right / middle pan; Phaser must not also handle it. */
  private spacePanPointerId: number | null = null;
  private readonly cameraControl = new CameraControl({
    minZoom: EDITOR_MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    constrainFit: 'overlap',
  });
  private unbindOverlayTyping: (() => void) | null = null;
  private hover: Vec2 | null = null;
  private activeBendDrag: BendDrag | null = null;
  private dragActive = false;
  private marquee: { start: Vec2; current: Vec2; additive: boolean } | null = null;
  /** Set when a press lands on an already-selected object, so the drag moves the whole group. */
  private groupDrag: { last: Vec2; moved: boolean } | null = null;
  /** Captured on flag dragstart so a later pointerdown cannot flip a one-pole drag into a gate move. */
  private flagDrag: { checkpointId: string; flag: CheckpointFlag; moveGate: boolean } | null = null;
  private groupScale: { origin: Vec2 } | null = null;
  private groupRotate: {
    origin: Vec2;
    startAngle: number;
    appliedRad: number;
  } | null = null;
  private playable = false;
  private testBoats: TestChoice[] = [];
  private recentMaps: RecentMap[] = [];
  private logVisible = false;
  private logFilter: EditorLogFilter = 'both';
  private logEntries: EditorLogEntry[] = [];
  private logFlashEntries: EditorLogEntry[] = [];
  private readonly logSeenIssues = new Set<string>();
  private logAdded = 0;
  private lastErrorCount = 0;
  private lastWarningCount = 0;

  private readonly onGlobalKeyDown = (event: KeyboardEvent): void => {
    if (overlayCapturesKeys()) {
      return;
    }

    const ctrlOrCmd = event.ctrlKey || event.metaKey;

    if (!ctrlOrCmd) {
      return;
    }

    const key = event.key.toLowerCase();

    if (key === 'z' && event.shiftKey) {
      event.preventDefault();
      this.handleRedo();
      return;
    }

    if (key === 'z') {
      event.preventDefault();
      this.handleUndo();
      return;
    }

    if (key === 'y') {
      event.preventDefault();
      this.handleRedo();
      return;
    }

    if (key === 's') {
      event.preventDefault();

      if (event.shiftKey) {
        this.handleSaveAs();
      } else {
        this.handleSave();
      }

      return;
    }

    if (event.code === 'KeyA' || key === 'a') {
      event.preventDefault();
      // Phaser does not capture modified keys; without this, the browser
      // highlights overlay labels (File, Edit, Map, Terrain, Water).
      window.getSelection()?.removeAllRanges();
      selectAll(this.session, this.map, this.hiddenRouteIds);
      this.redraw();
      return;
    }

    if (key === 'c') {
      event.preventDefault();
      copySelection(this.session, this.map);
      return;
    }

    if (key === 'x') {
      event.preventDefault();
      this.mutate(() => cutSelection(this.session, this.map));
      return;
    }

    if (key === 'v') {
      event.preventDefault();
      this.mutate(() => pasteFromClipboard(this.session, this.map));
      return;
    }

    if (key === 'd') {
      event.preventDefault();
      this.mutate(() => duplicateSelection(this.session, this.map));
    }
  };

  constructor() {
    super('EditorScene');
  }

  /**
   * `map` is set when returning from a test-play or when the start screen opens a catalog
   * entry. `fileName` binds that catalog file so DEV Save overwrites it.
   */
  create(data: { map?: MapDocument; fileName?: string } = {}): void {
    setPlayCursor(false);
    this.leaving = false;
    this.pauseMenu = null;
    this.map = data.map ?? createEmptyMap();
    this.columnVisibleKey = '';
    this.columnVisibleIds = null;
    this.session = createSession();
    this.history = createHistory();

    // The scene instance is reused across test-play round-trips, so drop display-object caches
    // whose children were destroyed on shutdown. Keeping them would leave badge numbers and
    // flag handles pointing at dead Text/Zone/Image objects.
    const editorPrefs = loadEditorSettings();
    this.previewLighting = editorPrefs.previewLighting;
    this.showElevation = editorPrefs.showElevation;
    this.showGrid = editorPrefs.showGrid;
    this.referenceImageAlpha = editorPrefs.referenceImageAlpha;
    this.referenceImage = null;
    this.referenceDrag = null;
    this.showScale = editorPrefs.showScale;
    this.showBoat = editorPrefs.showBoat;
    this.boatHeld = false;
    this.boatGuide = null;
    this.boatGuideScreen = null;
    this.showMinimap = editorPrefs.showMinimap;
    this.showPerf = editorPrefs.showPerf || RENDER_FLAGS.perf;
    this.gridDrawnFor = '';
    this.checkpointsDrawnFor = '';
    this.showRoutes = editorPrefs.showRoutes;
    this.applyPersistedRouteVisibility();
    setSnapToGrid(this.session, editorPrefs.snapToGrid);
    setDraftTerraceLevels(this.session, editorPrefs.terraceLevels);
    setDraftTerracePeakM(this.session, editorPrefs.terracePeakM);
    setDraftTerraceRadius(this.session, editorPrefs.terraceRadius);
    setDraftTerraceColorVariation(this.session, editorPrefs.terraceColorVariation);
    setDraftTerraceJitter(this.session, editorPrefs.terraceJitter);
    setDraftTerraceElevRand(this.session, editorPrefs.terraceElevRand);
    setDraftTerraceRadiusRand(this.session, editorPrefs.terraceRadiusRand);
    this.sunEditing = false;
    this.lookEditing = false;
    this.colorEditing = false;
    this.cameraControl.reset();
    this.cameraControl.setWorldBounds(this.map.bounds);
    this.mapLayersZoom = EDITOR_DEFAULT_ZOOM;
    this.cancelSunBake();
    this.checkpointLabels.clear();
    this.routeLabels.clear();
    this.islandOverlays?.destroy();
    this.islandOverlays = null;
    this.checkpointPoles?.destroy();
    this.checkpointPoles = null;
    this.elevatedLayers?.destroy();
    this.elevatedLayers = null;
    this.checkpointFlagHandles.clear();
    this.handles = [];
    this.startAreaLabel = null;
    this.parkAreaLabel = null;
    this.groupScale = null;
    this.groupRotate = null;
    this.playable = false;
    this.recentMaps = editorPrefs.recentMaps;
    this.logVisible = editorPrefs.logVisible;
    this.logFilter = editorPrefs.logFilter;
    this.logEntries = [];
    this.logFlashEntries = [];
    this.logSeenIssues.clear();
    this.logAdded = 0;
    this.lastErrorCount = 0;
    this.lastWarningCount = 0;
    this.game.canvas.parentElement?.removeAttribute('data-checkpoint-badges');
    this.game.canvas.parentElement?.removeAttribute('data-route-labels');
    this.game.canvas.parentElement?.removeAttribute('data-reference-image');

    this.fpsClock = createFpsClock();

    showOverlay(mountOverlay());
    this.fpsHud = mountFps();
    pushFps(this.fpsHud, this.game.loop, this.fpsClock);
    this.scenePerf?.destroy();
    this.scenePerf = new ScenePerf(this.game);
    this.perfPanel?.destroy();
    this.perfPanel = null;
    this.syncPerfPanel();
    this.toolbar = mountEditorToolbar({
      getState: () => ({
        tool: this.session.tool,
        draftMaterial: this.session.draftMaterial,
        draftDepth: this.session.draftDepth,
        draftProp: this.session.draftProp,
        draftTerraceKind: this.session.draftTerraceKind,
        draftTerraceLevels: this.session.draftTerraceLevels,
        draftTerracePeakM: this.session.draftTerracePeakM,
        draftTerraceRadius: this.session.draftTerraceRadius,
        draftTerraceColor: this.session.draftTerraceColor,
        draftTerraceColorVariation: this.session.draftTerraceColorVariation,
        draftTerraceJitter: this.session.draftTerraceJitter,
        draftTerraceElevRand: this.session.draftTerraceElevRand,
        draftTerraceRadiusRand: this.session.draftTerraceRadiusRand,
        selection: this.session.selection,
        snapToGrid: this.session.snapToGrid,
        showScale: this.showScale,
        showBoat: this.showBoat,
        showMinimap: this.showMinimap,
        showPerf: this.showPerf,
        showRoutes: this.showRoutes,
        hiddenRouteIds: [...this.hiddenRouteIds],
        routeDrafting: this.session.tool === 'draw-route' && this.session.draft.length > 0,
        previewLighting: this.previewLighting,
        showElevation: this.showElevation,
        showCapFlock: this.map.look.capFlockEnabled,
        capFlockAmount: this.map.look.capFlockAmount,
        waterlineHintAlpha: this.map.look.waterlineHintAlpha,
        showGrid: this.showGrid,
        hasReferenceImage: hasReferenceImage(),
        showReferenceImage: getReferenceImageSession()?.visible === true,
        referenceImageAlpha: this.referenceImageAlpha,
        sun: { ...this.map.environment.sun },
        look: { ...this.map.look, aimChrome: this.map.look.aimChrome },
        canUndo: canUndo(this.history),
        canRedo: canRedo(this.history),
        fileName: this.currentFileName(),
        mapName: this.map.name,
        mapRevision: this.mapRevision,
        playable: this.playable,
        testBoats: this.testBoats,
        recentMaps: this.recentMaps,
        canDeleteMap: canDeleteRepoMap(this.repoFile),
        logVisible: this.logVisible,
        logFilter: this.logFilter,
        logBadge: this.currentLogBadge(),
      }),
      actions: {
        setTool: (tool) => {
          this.applyTool(tool);
          this.game.canvas.focus();
        },
        setDraftMaterial: (material) => {
          setDraftMaterial(this.session, material);
          this.toolbar?.sync();
        },
        setDraftDepth: (depth) => {
          setDraftDepth(this.session, depth);
          this.toolbar?.sync();
        },
        setDraftProp: (prop) => {
          setDraftProp(this.session, prop);
          this.toolbar?.sync();
        },
        setDraftTerraceKind: (kind) => {
          setDraftTerraceKind(this.session, kind);
          this.toolbar?.sync();
        },
        setDraftTerraceLevels: (levels) => {
          setDraftTerraceLevels(this.session, levels);
          this.persistEditorSettings();
          this.toolbar?.sync();
        },
        setDraftTerracePeakM: (peakM) => {
          setDraftTerracePeakM(this.session, peakM);
          this.persistEditorSettings();
          this.toolbar?.sync();
        },
        setDraftTerraceRadius: (radius) => {
          setDraftTerraceRadius(this.session, radius);
          this.persistEditorSettings();
          this.toolbar?.sync();
        },
        setDraftTerraceColor: (color) => {
          setDraftTerraceColor(this.session, color);
          this.toolbar?.sync();
        },
        setDraftTerraceColorVariation: (variation) => {
          setDraftTerraceColorVariation(this.session, variation);
          this.persistEditorSettings();
          this.toolbar?.sync();
        },
        setDraftTerraceJitter: (jitter) => {
          setDraftTerraceJitter(this.session, jitter);
          this.persistEditorSettings();
          this.toolbar?.sync();
        },
        setDraftTerraceElevRand: (elevRand) => {
          setDraftTerraceElevRand(this.session, elevRand);
          this.persistEditorSettings();
          this.toolbar?.sync();
        },
        setDraftTerraceRadiusRand: (radiusRand) => {
          setDraftTerraceRadiusRand(this.session, radiusRand);
          this.persistEditorSettings();
          this.toolbar?.sync();
        },
        setSnapToGrid: (snap) => {
          setSnapToGrid(this.session, snap);
          this.persistEditorSettings();
          this.toolbar?.sync();
        },
        setShowScale: (on) => this.setScaleVisible(on),
        setShowBoat: (on) => this.setBoatGuideVisible(on),
        setShowMinimap: (on) => this.setMinimapVisible(on),
        setShowPerf: (on) => this.setPerfVisible(on),
        setShowRoutes: (on) => this.setRoutesVisible(on),
        onToggleRouteVisibility: (routeId) => this.toggleRouteVisibility(routeId),
        onDeleteRoute: (routeId) => {
          this.mutate(() => removeRoute(this.map, routeId));
        },
        setPreviewLighting: (on) => this.setLightingPreview(on),
        setShowElevation: (on) => this.setElevationVisible(on),
        setShowCapFlock: (on) => this.setShowCapFlock(on),
        setCapFlockAmount: (amount) => this.setCapFlockAmount(amount),
        setWaterlineHintAlpha: (alpha) => this.setWaterlineHintAlpha(alpha),
        setShowGrid: (on) => this.setShowGrid(on),
        setShowReferenceImage: (on) => this.setShowReferenceImage(on),
        setReferenceImageAlpha: (alpha) => this.setReferenceImageAlpha(alpha),
        onReferenceImageAlphaCommit: () => this.persistEditorSettings(),
        onLoadReferenceImage: () => this.handleLoadReferenceImage(),
        onClearReferenceImage: () => this.handleClearReferenceImage(),
        onAimChromeLive: (patch) => this.applyAimChromeLive(patch),
        onSunLive: (sun) => this.applySunLive(sun),
        onSunCommit: () => this.commitSunEdit(),
        onLookLive: (look) => this.applyLookLive(look),
        onLookCommit: () => this.commitLookEdit(),
        onLookCancel: () => this.cancelLookEdit(),
        onUndo: () => this.handleUndo(),
        onRedo: () => this.handleRedo(),
        onNew: () => this.handleNew(),
        onSave: () => this.handleSave(),
        onSaveAs: () => this.handleSaveAs(),
        onSaveAndClose: () => this.handleSaveAndClose(),
        onExport: () => this.handleExport(),
        onOpen: () => this.handleOpen(),
        onOpenRecent: (id) => {
          void this.openRecentMap(id);
        },
        onImport: () => this.handleImport(),
        onDeleteMap: () => this.handleDeleteMap(),
        onTestBoat: (id) => {
          void this.handleTestBoat(id);
        },
        refreshTestBoats: () => {
          void this.refreshTestBoats();
        },
        onRename: (raw) => {
          this.mutate(() => setMapName(this.map, raw));
        },
        onColorLive: (color) => this.applyColorLive(color),
        onColorCommit: () => this.commitColorEdit(),
        onColorCancel: () => this.cancelColorEdit(),
        onSetCornerRadius: (radius) => this.mutate(() => this.setSelectedCornerRadius(radius)),
        onStraightenEdge: () => this.mutate(() => this.straightenSelectedEdge()),
        onSetMaterial: (material) => this.mutate(() => this.setSelectedMaterial(material)),
        onSetElevation: (elevation) => this.mutate(() => this.setSelectedElevation(elevation)),
        onSetSkirtM: (skirtM) => this.mutate(() => this.setSelectedSkirtM(skirtM)),
        onScatterProps: () => this.mutate(() => this.scatterSelectedProps()),
        onClearProps: () => this.mutate(() => this.clearSelectedProps()),
        onSetDepth: (depth) => this.mutate(() => this.setSelectedDepth(depth)),
        onSetCheckpointOrder: (order) => this.mutate(() => this.setSelectedCheckpointOrder(order)),
        onSetCheckpointGoal: (goal) => this.mutate(() => this.setSelectedCheckpointGoal(goal)),
        onSetGateWidth: (width) => this.mutate(() => this.setSelectedGateWidth(width)),
        onDuplicate: () => this.mutate(() => duplicateSelection(this.session, this.map)),
        onDelete: () => this.mutate(() => deleteSelection(this.session, this.map)),
        getMap: () => this.map,
        onSetMapMeta: (meta: MapMeta) => {
          this.mutate(() => setMapMeta(this.map, meta));
        },
        onSetMapSize: (size) => {
          this.mutate(() => setMapSize(this.map, size));
          this.applyMapBounds({ recenter: false });
        },
        onSetLaps: (laps) => {
          this.mutate(() => setLaps(this.map, laps));
        },
        onSetEnvironment: (patch) => {
          this.mutate(() => updateEnvironment(this.map.environment, patch));
        },
        setLogVisible: (on) => {
          this.logVisible = on;
          this.persistEditorSettings();
          this.toolbar?.sync();
          this.validation?.sync();
        },
        setLogFilter: (filter) => {
          this.logFilter = filter;
          this.logVisible = true;
          this.persistEditorSettings();
          this.toolbar?.sync();
          this.validation?.sync();
        },
      },
    });
    this.scaleMeter = mountSceneScale('editor');
    this.validation = mountEditorValidation({
      getValidation: () => validateMap(this.map),
      getLog: () => ({
        visible: this.logVisible,
        filter: this.logFilter,
        entries: this.logEntries,
        flashId: this.logAdded,
        flashEntries: this.logFlashEntries,
      }),
      actions: {
        onReveal: (ref) => this.revealRef(ref),
        onShowLog: () => {
          this.logVisible = true;
          this.persistEditorSettings();
          this.toolbar?.sync();
          this.validation?.sync();
        },
      },
    });

    // The scene instance is reused after test-play. Reset so a leftover minimap camera
    // cannot linger as `main`.
    this.spaceDown = false;
    this.spacePanPointerId = null;
    this.cameras.resetAll();
    this.cameras.main.setZoom(EDITOR_DEFAULT_ZOOM);
    this.applyMapBounds();

    this.gridGraphics = this.add.graphics().setDepth(GRID_DEPTH);
    this.waterSheetGraphics = this.add.graphics().setDepth(WATER_SHEET_DEPTH);
    this.lighting = new SunLighting(this, 'editor');
    // Retained terrain geometry, same as play (docs/PERFORMANCE.md). A drag rebuilds only
    // the body whose outline changed (`ColumnPaint.fillKey`).
    const staticTerrain = RENDER_FLAGS.staticTerrain && StaticMesh.isSupported(this);
    const terrainLayer = (depth: number): TerrainLayer =>
      (staticTerrain
        ? this.add.existing(new StaticMesh(this))
        : this.add.graphics()
      ).setDepth(depth);
    this.waterGraphics = terrainLayer(WATER_DEPTH);
    this.waterChromeGraphics = terrainLayer(WATER_DEPTH + 0.01);
    this.skirtGraphics = terrainLayer(SKIRT_DEPTH);
    this.landGraphics = this.add.graphics().setDepth(LAND_DEPTH);
    this.waterlineHintGraphics = terrainLayer(WATERLINE_HINT_DEPTH);
    this.elevatedLayers = new ElevatedBodyGraphicsPool(this, LAND_DEPTH, {
      staticGeometry: staticTerrain,
    });
    this.islandOverlays = new IslandOverlayPool(this, {
      base: CHECKPOINT_DEPTH,
      span: PROP_POLE_Y_SORT_SPAN,
    });
    this.checkpointPoles = new CheckpointPolePool(
      this,
      {
        base: CHECKPOINT_DEPTH,
        span: PROP_POLE_Y_SORT_SPAN,
      },
      { staticGeometry: staticTerrain },
    );
    this.rockGraphics = this.add.graphics().setDepth(ROCK_DEPTH);
    this.startAreaGraphics = this.add.graphics().setDepth(START_AREA_DEPTH);
    this.parkAreaGraphics = this.add.graphics().setDepth(PARK_AREA_DEPTH);
    this.checkpointSubmergedGraphics = terrainLayer(CHECKPOINT_SUBMERGED_DEPTH);
    this.checkpointWaterlineGraphics = terrainLayer(CHECKPOINT_WATERLINE_DEPTH);
    this.checkpointGraphics = terrainLayer(CHECKPOINT_DEPTH);
    this.routeGraphics = this.add.graphics().setDepth(ROUTE_DEPTH);
    this.draftGraphics = this.add.graphics().setDepth(DRAFT_DEPTH);
    this.overlayGraphics = this.add.graphics().setDepth(OVERLAY_DEPTH);
    this.boatGuide = this.add
      .image(0, 0, 'boat')
      .setDisplaySize(BOAT_GUIDE_LENGTH_PX, BOAT_GUIDE_BEAM_PX)
      .setOrigin(0.5)
      .setDepth(BOAT_GUIDE_DEPTH)
      .setVisible(false);
    this.hydrateReferenceImage();

    // After the shared layers exist, so the ignore list has something to hide.
    this.minimap = new EditorMinimap(this, this.map);
    this.minimap.setVisible(this.showMinimap);
    this.scaleMeter?.setVisible(this.showScale);
    this.game.canvas.parentElement?.setAttribute(
      'data-minimap',
      this.showMinimap ? 'true' : 'false',
    );
    this.game.canvas.parentElement?.setAttribute(
      'data-show-routes',
      this.showRoutes ? 'true' : 'false',
    );

    this.redrawGrid();
    this.syncScale();
    this.mapLayersZoom = this.cameras.main.zoom;

    this.input.mouse?.disableContextMenu();
    this.bindInput();
    this.redraw();

    this.scale.on(Phaser.Scale.Events.RESIZE, this.onGameResize, this);
    window.addEventListener('keydown', this.onWindowKeyDown, { capture: true });
    window.addEventListener('keyup', this.onWindowKeyUp, { capture: true });
    window.addEventListener('blur', this.onWindowBlur);
    window.addEventListener('pointerdown', this.onNativePointerDown, { capture: true });
    window.addEventListener('pointermove', this.onNativePointerMove, { capture: true });
    window.addEventListener('pointerup', this.onNativePointerUp, { capture: true });
    window.addEventListener('pointercancel', this.onNativePointerUp, { capture: true });
    window.addEventListener('beforeunload', this.onWindowBeforeUnload);

    const escapeToken = setEscapeOwner({
      onEscape: () => this.handleEscape(),
    });

    this.unbindOverlayTyping = bindOverlayTyping((typing) => {
      const kb = this.input.keyboard;

      if (!kb) {
        return;
      }

      kb.enabled = !typing;
      kb.manager.preventDefault = !typing;
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      clearEscapeOwner(escapeToken);
      this.destroyPause();
      setEditorCursor(null);
      this.scale.off(Phaser.Scale.Events.RESIZE, this.onGameResize, this);
      window.removeEventListener('keydown', this.onWindowKeyDown, { capture: true });
      window.removeEventListener('keyup', this.onWindowKeyUp, { capture: true });
      window.removeEventListener('blur', this.onWindowBlur);
      window.removeEventListener('pointerdown', this.onNativePointerDown, { capture: true });
      window.removeEventListener('pointermove', this.onNativePointerMove, { capture: true });
      window.removeEventListener('pointerup', this.onNativePointerUp, { capture: true });
      window.removeEventListener('pointercancel', this.onNativePointerUp, { capture: true });
      window.removeEventListener('beforeunload', this.onWindowBeforeUnload);
      this.persistWorkingCopy();
      this.unbindOverlayTyping?.();
      this.unbindOverlayTyping = null;
      this.dragPaint.cancel();
      this.fpsHud?.destroy();
      this.fpsHud = null;
      this.scenePerf?.destroy();
      this.scenePerf = null;
      this.perfPanel?.destroy();
      this.perfPanel = null;
      this.toolbar?.destroy();
      this.toolbar = null;
      this.scaleMeter?.destroy();
      this.scaleMeter = null;
      this.validation?.destroy();
      this.validation = null;
      this.minimap?.destroy();
      this.minimap = null;
      this.game.canvas.parentElement?.removeAttribute('data-minimap');
      this.cancelSunBake();
      this.sunEditing = false;
      this.lighting?.destroy();
      this.lighting = null;
      this.checkpointLabels.clear();
      this.routeLabels.clear();
      this.checkpointFlagHandles.clear();
      this.handles = [];
      this.startAreaLabel = null;
      this.parkAreaLabel = null;
      this.islandOverlays?.destroy();
      this.islandOverlays = null;
      this.checkpointPoles?.destroy();
      this.checkpointPoles = null;
      this.elevatedLayers?.destroy();
      this.elevatedLayers = null;
    });

    this.game.canvas.parentElement?.setAttribute('data-ready', 'true');
    void this.refreshTestBoats();
    this.syncSelectionHandleAttr();
    this.syncSelectionGizmoAttr();

    // Read the draft before bindFile writes the catalog name over the stored filename.
    const pendingDraft = loadResumableDraft();

    if (data.fileName) {
      this.bindFile(null, data.fileName);
      this.rememberRecentMap();
    } else if (!data.map) {
      this.fileHandle = null;
      this.repoFile = null;
      this.importedName = null;
    }

    // Returning from a test-play already carries the live document, so there is nothing to resume.
    // Keep the pre-race baseline so unsaved edits stay dirty.
    if (!data.map) {
      this.rememberBaseline();
      void this.offerSavedMap(pendingDraft);
    } else if (data.fileName) {
      this.rememberBaseline();
      void this.offerSavedMap(pendingDraft, data.fileName);
    }

    this.syncCursor();
  }

  update(): void {
    if (!this.sys.isActive() || !this.gridGraphics?.scene) {
      return;
    }

    this.syncBoatGuide();
    this.cullOffscreenColumns();
    this.redrawGrid(false);
    pushFps(this.fpsHud, this.game.loop, this.fpsClock);
    this.pushPerf();
    // The view rectangle tracks a camera that pan and zoom move without going through redraw().
    this.minimap?.draw(this.map, {
      hiddenRouteIds: this.hiddenRouteIds,
      selectedRouteIds: new Set(selectedIds(this.session.selection, 'route')),
    });
  }

  /** Recentring is right when a different map is loaded, but jarring while resizing the current one. */
  private applyMapBounds({ recenter = true }: { recenter?: boolean } = {}): void {
    const { width, height } = this.map.bounds;
    const cam = this.cameras.main;

    // The camera is constrained by constrainCamera() instead of Phaser's own clamp; see view.ts.
    cam.removeBounds();
    cam.setBackgroundColor(VOID_COLOR);

    if (recenter) {
      cam.centerOn(width / 2, height / 2);
    }

    this.minimap?.layout(this.map);
    this.constrainCamera();
    this.syncLighting();
  }

  private constrainCamera(): void {
    this.cameraControl.setWorldBounds(this.map.bounds);
    this.cameraControl.constrain(this.cameras.main);
  }

  /**
   * Offered only for a dirty draft left by an unexpected close. A catalog Edit of a different
   * file is an intentional switch, so that draft stays put until this map is edited.
   */
  private async offerSavedMap(
    draft: ResumableDraft | null,
    onlyFile?: string,
  ): Promise<void> {
    if (!draft) {
      return;
    }

    if (onlyFile !== undefined && draft.fileName !== onlyFile) {
      return;
    }

    if (serializeMap(draft.map) === serializeMap(this.map)) {
      return;
    }

    this.draftOfferPending = true;

    let resume = false;

    try {
      resume = await confirmDialog({
        title: 'Resume editing?',
        message: 'You have unsaved changes from a previous session.',
        confirmLabel: 'Resume',
        cancelLabel: 'Start fresh',
      });
    } finally {
      this.draftOfferPending = false;
    }

    if (!resume) {
      clearResumableDraft();

      if (!this.repoFile) {
        saveRepoFileName(null);
      }

      this.rememberBaseline();
      return;
    }

    this.bindFile(null, draft.fileName);
    this.map = draft.map;
    this.columnVisibleKey = '';
    this.columnVisibleIds = null;
    this.rememberRecentMap();
    this.history = createHistory();
    this.resetSessionState();
    this.applyPersistedRouteVisibility();
    this.touchMap();
    this.applyMapBounds();
    this.redraw();
    this.syncLighting();
  }

  // --- Input wiring ---------------------------------------------------

  private bindInput(): void {
    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.on('pointermove', this.onPointerMove, this);
    this.input.on('gameout', this.onPointerLeaveCanvas, this);
    this.input.on('pointerup', this.onPointerUp, this);
    this.input.on('pointerupoutside', this.onPointerUp, this);
    this.input.on('wheel', this.onWheel, this);
    this.input.on('dragstart', this.onDragStart, this);
    this.input.on('drag', this.onDrag, this);
    this.input.on('dragend', this.onDragEnd, this);

    const keyboard = this.input.keyboard;

    if (!keyboard) {
      return;
    }

    // Escape is owned by the pause router (draft, deselect, then the menu). Capture still
    // stops the browser from handling the key. Space stays on window so Phaser capture
    // cannot abort a pointer drag. Digits and delete stay uncaptured for number fields.
    keyboard.addCapture('ESC');

    keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER, false).on('down', () => {
      if (overlayCapturesKeys()) {
        return;
      }

      if (canFinishRoute(this.session)) {
        this.hover = null;
        this.mutate(() => {
          finishRoute(this.session, this.map);
        });
        return;
      }

      if (!canCloseDraft(this.session)) {
        return;
      }

      this.hover = null;
      this.mutate(() => {
        closeDraft(this.session, this.map);
      });
    });

    const removeSelected = (): void => {
      if (overlayCapturesKeys()) {
        return;
      }

      this.mutate(() => deleteSelection(this.session, this.map));
    };

    keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.DELETE, false).on('down', removeSelected);
    keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.BACKSPACE, false).on('down', removeSelected);

    const bindNudge = (code: number, dx: number, dy: number): void => {
      keyboard.addKey(code, false).on('down', (event: KeyboardEvent) => {
        if (overlayCapturesKeys()) {
          return;
        }

        this.nudgeSelection(dx, dy, event.shiftKey);
      });
    };

    bindNudge(Phaser.Input.Keyboard.KeyCodes.LEFT, -1, 0);
    bindNudge(Phaser.Input.Keyboard.KeyCodes.RIGHT, 1, 0);
    bindNudge(Phaser.Input.Keyboard.KeyCodes.UP, 0, -1);
    bindNudge(Phaser.Input.Keyboard.KeyCodes.DOWN, 0, 1);

    const bindTool = (code: number, tool: EditorTool): void => {
      keyboard.addKey(code, false).on('down', (event: KeyboardEvent) => {
        if (overlayCapturesKeys() || event.ctrlKey || event.metaKey || event.altKey) {
          return;
        }

        this.applyTool(tool);
      });
    };

    bindTool(Phaser.Input.Keyboard.KeyCodes.V, 'select');
    bindTool(Phaser.Input.Keyboard.KeyCodes.H, 'hand');
    bindTool(Phaser.Input.Keyboard.KeyCodes.S, 'scale');
    bindTool(Phaser.Input.Keyboard.KeyCodes.Q, 'rotate');
    bindTool(Phaser.Input.Keyboard.KeyCodes.I, 'draw-island');
    bindTool(Phaser.Input.Keyboard.KeyCodes.R, 'draw-rock');
    bindTool(Phaser.Input.Keyboard.KeyCodes.L, 'draw-terrace');
    bindTool(Phaser.Input.Keyboard.KeyCodes.W, 'draw-water');
    bindTool(Phaser.Input.Keyboard.KeyCodes.C, 'place-checkpoint');
    bindTool(Phaser.Input.Keyboard.KeyCodes.T, 'edit-start-area');
    bindTool(Phaser.Input.Keyboard.KeyCodes.K, 'edit-park-area');
    bindTool(Phaser.Input.Keyboard.KeyCodes.O, 'draw-route');
    bindTool(Phaser.Input.Keyboard.KeyCodes.ONE, 'select');
    bindTool(Phaser.Input.Keyboard.KeyCodes.TWO, 'draw-island');
    bindTool(Phaser.Input.Keyboard.KeyCodes.THREE, 'draw-rock');
    bindTool(Phaser.Input.Keyboard.KeyCodes.FIVE, 'draw-water');
    bindTool(Phaser.Input.Keyboard.KeyCodes.SIX, 'place-checkpoint');
    bindTool(Phaser.Input.Keyboard.KeyCodes.SEVEN, 'edit-start-area');
    bindTool(Phaser.Input.Keyboard.KeyCodes.P, 'place-prop');
    bindTool(Phaser.Input.Keyboard.KeyCodes.X, 'erase-prop');
  }

  private onGameResize(gameSize: Phaser.Structs.Size): void {
    this.cameras.resize(gameSize.width, gameSize.height);
    // `cameras.resize` stretches every camera to the canvas, so the minimap has to reclaim its
    // corner afterwards rather than before.
    this.minimap?.layout(this.map);
    this.constrainCamera();
    this.redraw();
  }

  /** Centres the main camera on the world point under the pointer's position in the minimap. */
  private jumpToMinimap(pointer: Phaser.Input.Pointer): void {
    const point = this.minimap?.worldPoint(pointer.x, pointer.y);

    if (!point) {
      return;
    }

    this.cameras.main.centerOn(point.x, point.y);
    this.constrainCamera();
  }

  private applyTool(tool: EditorTool): void {
    if (
      (tool === 'place-prop' || tool === 'scale' || tool === 'rotate') &&
      this.showElevation
    ) {
      return;
    }

    setTool(this.session, tool);
    this.hover = null;
    this.activeBendDrag = null;
    this.marquee = null;
    this.groupDrag = null;
    this.groupScale = null;
    this.groupRotate = null;
    this.syncCursor();
    this.redraw();
  }

  /** Arrow keys move the selection by whole world pixels; Shift makes the step coarse. */
  private nudgeSelection(dx: number, dy: number, coarse: boolean): void {
    if (this.session.selection.refs.length === 0) {
      return;
    }

    // With snapping on, a nudge steps a whole grid cell; the 1 px and 10 px steps are for
    // free-hand placement and would be swallowed by the snap anyway.
    const step = this.session.snapToGrid ? SNAP_GRID : coarse ? NUDGE_COARSE_PX : NUDGE_STEP_PX;

    this.mutate(() => moveSelection(this.session, this.map, { x: dx * step, y: dy * step }));
  }

  private worldRadius(screenPx: number): number {
    return screenToWorld(screenPx, this.cameras.main.zoom);
  }

  private poleScale(): number {
    return poleWorldScale(this.cameras.main.zoom);
  }

  private worldPoint(pointer: Phaser.Input.Pointer): Vec2 {
    return { x: pointer.worldX, y: pointer.worldY };
  }

  private spaceHeld(): boolean {
    return !isTypingInField() && this.spaceDown;
  }

  private isPanPointer(pointer: Phaser.Input.Pointer): boolean {
    return (
      this.session.tool === 'hand' ||
      this.spaceHeld() ||
      pointer.middleButtonDown() ||
      pointer.rightButtonDown() ||
      pointer.button === 1 ||
      pointer.button === 2
    );
  }

  private beginPanAt(x: number, y: number): void {
    this.cameraControl.beginPan(this.cameras.main, x, y);
    this.syncCursor();
  }

  private beginPan(pointer: Phaser.Input.Pointer): void {
    this.beginPanAt(pointer.x, pointer.y);
  }

  private applyPanAt(x: number, y: number): void {
    this.cameraControl.applyPan(this.cameras.main, x, y);
  }

  private canvasPointFromClient(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.game.canvas.getBoundingClientRect();
    const width = rect.width || 1;
    const height = rect.height || 1;

    return {
      x: ((clientX - rect.left) / width) * this.scale.width,
      y: ((clientY - rect.top) / height) * this.scale.height,
    };
  }

  private isOverlayPointerTarget(target: EventTarget | null): boolean {
    return target instanceof Element && Boolean(target.closest('.ui-chrome, [role="dialog"]'));
  }

  private blurEditorChrome(): void {
    const active = document.activeElement;

    if (active instanceof HTMLElement && active.closest('.ui-chrome') && !isTypingInField()) {
      active.blur();
    }
  }

  private endSpacePan(pointerId: number): void {
    if (this.spacePanPointerId === pointerId) {
      try {
        this.game.canvas.releasePointerCapture(pointerId);
      } catch {
        // Capture may already have been released by pointercancel.
      }

      this.spacePanPointerId = null;
    }

    this.cameraControl.endPan();
    this.syncCursor();
  }

  /**
   * Space, right-button, and middle-button pan are owned by native pointer events.
   * Phaser ignores `defaultPrevented` mouse events; Select handles also swallow a
   * right-press before the scene `pointerdown` can arm a pan. Capture-phase
   * preventDefault + setPointerCapture keeps the gesture. Do not focus the canvas
   * here — that aborts the pointer.
   */
  private readonly onNativePointerDown = (event: PointerEvent): void => {
    if (!this.sys.isActive() || this.spacePanPointerId !== null) {
      return;
    }

    const spaceLeft = event.button === 0 && this.spaceHeld();
    const auxPan = event.button === 1 || event.button === 2;

    if (!spaceLeft && !auxPan) {
      return;
    }

    if (this.isOverlayPointerTarget(event.target)) {
      return;
    }

    event.preventDefault();
    const point = this.canvasPointFromClient(event.clientX, event.clientY);
    this.spacePanPointerId = event.pointerId;
    this.beginPanAt(point.x, point.y);

    try {
      this.game.canvas.setPointerCapture(event.pointerId);
    } catch {
      // Some synthetic clicks have no capture target; move/up still land on window.
    }
  };

  private readonly onNativePointerMove = (event: PointerEvent): void => {
    if (this.spacePanPointerId === null || event.pointerId !== this.spacePanPointerId) {
      return;
    }

    event.preventDefault();
    const point = this.canvasPointFromClient(event.clientX, event.clientY);
    this.noteBoatGuidePointer(point.x, point.y);
    this.applyPanAt(point.x, point.y);
  };

  private readonly onNativePointerUp = (event: PointerEvent): void => {
    if (this.spacePanPointerId === null || event.pointerId !== this.spacePanPointerId) {
      return;
    }

    event.preventDefault();
    this.endSpacePan(event.pointerId);
  };

  /** Space is a hold-to-pan modifier; it must not type into a scrub field or steal canvas focus. */
  private onSpaceKeyDown(event: KeyboardEvent): void {
    if (isTypingInField() || isPauseMenuOpen()) {
      return;
    }

    event.preventDefault();

    if (event.repeat) {
      return;
    }

    this.spaceDown = true;
    this.blurEditorChrome();
    const pointer = this.input.activePointer;

    if (pointer?.isDown && !this.cameraControl.panning) {
      this.beginPan(pointer);
    }

    this.syncCursor();
  }

  private readonly onWindowKeyDown = (event: KeyboardEvent): void => {
    if (isPauseMenuOpen()) {
      return;
    }

    if (event.code === 'Space') {
      this.onSpaceKeyDown(event);
      return;
    }

    if (event.code === 'KeyB') {
      this.onBoatKeyDown(event);
      return;
    }

    if (
      !event.repeat &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey &&
      !overlayCapturesKeys()
    ) {
      if (event.code === 'KeyE') {
        event.preventDefault();
        this.setElevationVisible(!this.showElevation);
        this.game.canvas.focus();
        return;
      }

      if (event.code === 'KeyG') {
        event.preventDefault();
        setSnapToGrid(this.session, !this.session.snapToGrid);
        this.persistEditorSettings();
        this.toolbar?.sync();
        this.game.canvas.focus();
        return;
      }
    }

    this.onGlobalKeyDown(event);
  };

  /** Hold B shows the hull. A text field, a modifier, or an open float keeps the key. */
  private onBoatKeyDown(event: KeyboardEvent): void {
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      overlayCapturesKeys()
    ) {
      return;
    }

    event.preventDefault();

    if (event.repeat || this.boatHeld) {
      return;
    }

    this.boatHeld = true;

    if (this.input.isOver) {
      const pointer = this.input.activePointer;
      this.noteBoatGuidePointer(pointer.x, pointer.y);
    }

    this.syncBoatGuide();
  }

  private releaseBoatHold(): void {
    if (!this.boatHeld) {
      return;
    }

    this.boatHeld = false;

    if (!this.showBoat) {
      this.boatGuideScreen = null;
    }

    this.syncBoatGuide();
  }

  private readonly onWindowBlur = (): void => {
    this.releaseBoatHold();
  };

  private boatGuideActive(): boolean {
    return this.showBoat || this.boatHeld;
  }

  private readonly onWindowKeyUp = (event: KeyboardEvent): void => {
    if (event.code === 'KeyB') {
      this.releaseBoatHold();
      return;
    }

    if (event.code !== 'Space') {
      return;
    }

    if (!isTypingInField()) {
      event.preventDefault();
    }

    this.spaceDown = false;
    this.syncCursor();
  };

  private syncCursor(): void {
    const tool = this.session.tool;
    setEditorCursor(tool);
    const hand = tool === 'hand' || this.spaceHeld() || this.cameraControl.panning;
    const cursor = this.cameraControl.panning
      ? 'grabbing'
      : hand
        ? 'grab'
        : cursorForEditorTool(tool);
    // Phaser restores to defaultCursor when leaving interactive handles — keep it in sync.
    this.input.setDefaultCursor(cursor);
    this.syncInteractiveCursors();
  }

  /** Phaser handle hover must use the black twin — never OS `pointer` (that sticks). */
  private syncInteractiveCursors(): void {
    const hover = this.cameraControl.panning
      ? 'grabbing'
      : this.session.tool === 'hand' || this.spaceHeld()
        ? 'grab'
        : hoverCursorForEditorTool(this.session.tool);

    for (const handle of this.handles) {
      if (handle.input) {
        handle.input.cursor = hover;
      }
    }

    for (const flags of this.checkpointFlagHandles.values()) {
      if (flags.a.input) {
        flags.a.input.cursor = hover;
      }

      if (flags.b.input) {
        flags.b.input.cursor = hover;
      }
    }
  }

  // --- Pointer handling -------------------------------------------------

  private pinchPointers(): [Phaser.Input.Pointer, Phaser.Input.Pointer] | null {
    return this.cameraControl.pinchPointers(this.input.manager.pointers);
  }

  private beginPinch(): boolean {
    if (!this.cameraControl.beginPinch(this.cameras.main, this.input.manager.pointers)) {
      return false;
    }

    this.minimapDrag = false;
    return true;
  }

  private applyPinch(): void {
    const pinch = this.cameraControl.applyPinch(this.input.manager.pointers);

    if (!pinch) {
      return;
    }

    this.applyEditorZoom(pinch.zoom, pinch.center.x, pinch.center.y);
  }

  private releasePinchPointers(): void {
    this.cameraControl.releasePinch(this.input.manager.pointers);
  }

  private onPointerDown(
    pointer: Phaser.Input.Pointer,
    currentlyOver: Phaser.GameObjects.GameObject[],
  ): void {
    if (this.pinchPointers()) {
      if (this.beginPinch()) {
        this.applyPinch();
      }

      return;
    }

    if (this.cameraControl.pinching || this.cameraControl.pinchConsumed) {
      return;
    }

    if (this.spacePanPointerId !== null) {
      return;
    }

    if (this.isPanPointer(pointer)) {
      this.beginPan(pointer);
      return;
    }

    if (!pointer.leftButtonDown()) {
      return;
    }

    // Checked before the handle test: the minimap sits over the world, and a press there is a
    // camera jump rather than anything to do with whatever object happens to be underneath.
    if (this.minimap?.contains(pointer.x, pointer.y)) {
      this.minimapDrag = true;
      this.jumpToMinimap(pointer);
      return;
    }

    // Interactive handles swallow the press; onDragStart has already resolved the selection for
    // them because Phaser dispatches drags ahead of this event. `dragActive` covers the case
    // where a zone started a drag but is missing from `currentlyOver`.
    if (currentlyOver.length > 0 || this.dragActive) {
      return;
    }

    const point = this.worldPoint(pointer);
    const tool = this.session.tool;

    if (tool === 'draw-route') {
      if (pointer.button !== 0) {
        return;
      }

      tryPlaceRoutePoint(this.session, this.map, point);
      this.hover = point;
      this.redraw();
      return;
    }

    if (DRAW_TOOLS.has(tool)) {
      const closeRadius = this.worldRadius(CLOSE_RADIUS_PX);
      const first = this.session.draft[0];
      const willClose = Boolean(first) && canCloseDraft(this.session) && isNear(first as Vec2, point, closeRadius);

      if (willClose) {
        this.beginEdit();
      }

      tryPlaceDrawPoint(this.session, this.map, point, closeRadius);

      if (willClose) {
        this.commitEdit();
      }

      this.hover = point;
      this.redraw();
      return;
    }

    if (tool === 'place-checkpoint') {
      if (this.session.checkpointDraft) {
        this.mutate(() => {
          finishCheckpointDraft(this.session, this.map, point);
        });
      } else {
        beginCheckpointDraft(this.session, point);
        this.hover = point;
        this.redrawCheckpoints();
      }

      return;
    }

    if (tool === 'place-prop') {
      if (this.showElevation) {
        return;
      }

      const elevate = this.columnsVisible();
      const island = findIslandAtPlateau(this.map, point, { elevate });

      if (island) {
        const waterline = waterlineFromPlateau(
          point,
          elevate ? worldElevation(this.map, island.id) : 0,
        );
        this.mutate(() => {
          plantIslandProp(this.map, island.id, this.session.draftProp, waterline);
        });
      }

      return;
    }

    if (tool === 'erase-prop') {
      const radius = this.worldRadius(SELECT_RADIUS_PX);
      const hit = findDecorationAt(this.map, point, radius, {
        elevate: this.columnsVisible(),
      });

      if (hit) {
        this.mutate(() => {
          removeIslandDecoration(this.map, hit.islandId, hit.decorationId);
        });
        return;
      }

      if (this.anyRouteVisible()) {
        const route = findRouteAt(this.map, point, radius, this.hiddenRouteIds);

        if (route) {
          this.mutate(() => {
            removeRoute(this.map, route.id);
          });
        }
      }

      return;
    }

    if (isSelectionTool(tool)) {
      const radius = this.worldRadius(SELECT_RADIUS_PX);
      const poleScale = this.poleScale();
      const owner = tool === 'select' ? this.ringOwnerFromSelection() : null;

      if (owner && ringSculptingEnabled(this.showElevation)) {
        const edgeIndex = this.findRingEdgeFor(owner, point, radius);

        if (edgeIndex !== null) {
          this.beginEdit();
          const ring = this.getRingFor(owner);
          const a = ring?.vertices[edgeIndex];
          const b = ring?.vertices[(edgeIndex + 1) % (ring?.vertices.length ?? 0)];

          if (ring && a && b && insertRingVertex(
            ring,
            edgeIndex,
            clampToBounds(closestPointOnSegment(point, a, b), this.map.bounds),
          )) {
            this.selectRingVertex(owner, edgeIndex + 1);
          }

          this.redraw();
          return;
        }
      }

      const routeRef = soleRef(this.session.selection);

      if (tool === 'select' && routeRef?.kind === 'route' && ringSculptingEnabled(this.showElevation)) {
        const segmentIndex = findRouteSegmentAt(this.map, routeRef.id, point, radius);

        if (segmentIndex !== null) {
          const route = findRoute(this.map, routeRef.id);
          const a = route?.points[segmentIndex];
          const b = route?.points[segmentIndex + 1];

          if (a && b) {
            this.beginEdit();
            if (
              insertRouteVertex(
                this.map,
                routeRef.id,
                segmentIndex,
                clampToBounds(closestPointOnSegment(point, a, b), this.map.bounds),
              )
            ) {
              selectRoute(this.session, routeRef.id, segmentIndex + 1);
            }
            this.redraw();
            return;
          }
        }
      }

      const additive = isAdditivePointer(pointer);

      // Empty water: drag draws a marquee. A tiny release is still a click (deselect).
      const elevate = this.columnsVisible();

      if (!refAt(this.map, point, radius, poleScale, elevate, this.hiddenRouteIds)) {
        this.marquee = { start: point, current: point, additive };
        this.redrawOverlay();
        return;
      }

      trySelectAt(
        this.session,
        this.map,
        point,
        radius,
        additive,
        poleScale,
        elevate,
        this.hiddenRouteIds,
      );
      this.groupDrag = additive ? null : { last: point, moved: false };
      this.redraw();
    }
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    this.noteBoatGuidePointer(pointer.x, pointer.y);

    if (this.cameraControl.pinching || this.beginPinch()) {
      this.applyPinch();
      return;
    }

    if (this.cameraControl.pinchConsumed || this.pinchPointers()) {
      return;
    }

    if (this.minimapDrag) {
      this.jumpToMinimap(pointer);
      return;
    }

    if (!this.cameraControl.panning && this.spaceHeld() && pointer.leftButtonDown()) {
      this.beginPan(pointer);
    }

    if (
      !this.cameraControl.panning &&
      (pointer.rightButtonDown() || pointer.middleButtonDown())
    ) {
      this.beginPan(pointer);
    }

    if (this.cameraControl.panning) {
      this.applyPanAt(pointer.x, pointer.y);
      return;
    }

    if (this.activeBendDrag) {
      const point = this.worldPoint(pointer);
      const owner = this.activeBendDrag.owner;
      this.moveRingBendFor(owner, this.activeBendDrag.edgeIndex, point);
      this.requestDragPaint(() => {
        this.redrawTerrainLayer(owner.kind);
        this.syncHandlePositions();
      });
      return;
    }

    if (this.marquee) {
      this.marquee.current = this.worldPoint(pointer);
      this.requestDragPaint(() => this.redrawOverlay());
      return;
    }

    if (this.groupDrag) {
      const point = this.worldPoint(pointer);
      const raw = { x: point.x - this.groupDrag.last.x, y: point.y - this.groupDrag.last.y };
      // A group has no single anchor to land on the grid, so snapping quantizes the step instead.
      const delta = this.session.snapToGrid ? snapDelta(raw, SNAP_GRID) : raw;

      if (delta.x === 0 && delta.y === 0) {
        return;
      }

      if (!this.groupDrag.moved) {
        this.beginEdit();
        this.groupDrag.moved = true;
      }

      // Advance by what was actually applied, so a clamped edge does not accumulate drift.
      const applied = moveSelection(this.session, this.map, delta);
      this.groupDrag.last = { x: this.groupDrag.last.x + applied.x, y: this.groupDrag.last.y + applied.y };
      this.requestDragPaint();
      return;
    }

    if (this.session.checkpointDraft) {
      this.hover = this.worldPoint(pointer);
      this.requestDragPaint(() => this.redrawCheckpoints());
      return;
    }

    if (
      (DRAW_TOOLS.has(this.session.tool) || this.session.tool === 'draw-route') &&
      this.session.draft.length > 0
    ) {
      this.hover = this.worldPoint(pointer);
      this.requestDragPaint(() => this.redrawDraft());
    }
  }

  private onPointerUp(_pointer: Phaser.Input.Pointer): void {
    const wasPinching = this.cameraControl.pinching || this.cameraControl.pinchConsumed;
    this.releasePinchPointers();

    if (wasPinching || this.spacePanPointerId !== null) {
      return;
    }

    const wasPanning = this.cameraControl.panning;
    this.cameraControl.endPan();
    this.syncCursor();

    if (this.minimapDrag) {
      this.minimapDrag = false;
      return;
    }

    if (wasPanning) {
      return;
    }

    if (this.activeBendDrag) {
      this.activeBendDrag = null;
      this.commitEdit();
      this.redraw();
      return;
    }

    if (this.marquee) {
      const { start, current, additive } = this.marquee;
      this.marquee = null;

      // Too small to be a deliberate marquee: treat it as a click on empty water.
      if (rectArea(normalizeRect(start, current)) < MIN_MARQUEE_AREA) {
        if (!additive) {
          clearSelection(this.session);
        }
      } else {
        selectInRect(
          this.session,
          this.map,
          start,
          current,
          additive,
          this.poleScale(),
          this.columnsVisible(),
          this.hiddenRouteIds,
        );
      }

      this.redraw();
      return;
    }

    if (this.groupDrag) {
      const moved = this.groupDrag.moved;
      this.groupDrag = null;

      if (moved) {
        this.commitEdit();
        this.redraw();
      }

      return;
    }

  }

  private onWheel(
    pointer: Phaser.Input.Pointer,
    _currentlyOver: Phaser.GameObjects.GameObject[],
    _deltaX: number,
    deltaY: number,
  ): void {
    if (this.boatHeld) {
      this.boatGuideHeading = boatGuideHeadingAfterWheel(this.boatGuideHeading, deltaY);
      this.syncBoatGuide();
      return;
    }

    const cam = this.cameras.main;
    const nextZoom = this.cameraControl.wheelZoom(cam.zoom, deltaY > 0);
    const anchor = this.minimap?.contains(pointer.x, pointer.y)
      ? { x: cam.width / 2, y: cam.height / 2 }
      : pointer;

    this.applyEditorZoom(nextZoom, anchor.x, anchor.y);
  }

  private applyEditorZoom(nextZoom: number, anchorX: number, anchorY: number): void {
    const cam = this.cameras.main;

    // Over the minimap the pointer sits on a world point the main view is not even showing, so
    // anchoring the zoom there would fling the camera. Hold the view centre instead.
    const anchor = this.minimap?.contains(anchorX, anchorY)
      ? { x: cam.width / 2, y: cam.height / 2 }
      : { x: anchorX, y: anchorY };

    if (!this.cameraControl.applyZoom(cam, nextZoom, anchor.x, anchor.y)) {
      return;
    }

    this.syncScale();
    this.redrawAuthoringChrome();

    if (noteZoom(this.mapLayersZoom, cam.zoom) === 'restroke') {
      this.redrawTerrain('chrome');
    }
  }

  /**
   * Seafloor, grid, and sheet follow the camera. `update()` calls this every frame with
   * `force` off, so a still camera costs one string compare; look and grid edits force it.
   */
  private redrawGrid(force = true): void {
    const cam = this.cameras.main;
    const options = { lighting: this.lighting, showGrid: this.showGrid };
    const signature = backdropSignature(cam, this.map, options);

    if (!force && signature === this.gridDrawnFor) {
      return;
    }

    this.gridDrawnFor = signature;
    applyMapBackdrop(this.gridGraphics, this.waterSheetGraphics, cam, this.map, options);
  }

  private syncScale(): void {
    this.scaleMeter?.setLabel(
      formatScaleLabel(gridCellMeters(this.cameras.main.zoom, PIXELS_PER_METER, GRID_SCREEN_PX)),
    );
  }

  // --- Dragging ----------------------------------------------------------

  /**
   * Phaser resolves drags before it dispatches the scene's `pointerdown`, so this is the first
   * handler to see a press on an interactive object and therefore the one that owns selection for
   * gate flags. A modifier-click edits the selection and deliberately does not arm a
   * drag, otherwise the toggle would be followed by a move of whatever survived it.
   */
  private onDragStart(
    pointer: Phaser.Input.Pointer,
    gameObject: Phaser.GameObjects.GameObject,
  ): void {
    if (
      this.cameraControl.pinching ||
      this.cameraControl.pinchConsumed ||
      this.isPanPointer(pointer) ||
      !isSelectionTool(this.session.tool)
    ) {
      return;
    }

    const kind = gameObject.getData('kind');
    const additive = isAdditivePointer(pointer);

    if (kind === 'reference-resize') {
      const overlay = getReferenceImageSession();
      const corner = this.groupHandleCorner(gameObject);

      if (!overlay || !corner) {
        return;
      }

      this.dragActive = true;
      this.referenceDrag = {
        kind: 'scale',
        origin: gizmoCornerPoint(referenceImageFrame(overlay), gizmoOppositeCorner(corner)),
        start: { x: overlay.x, y: overlay.y, width: overlay.width, height: overlay.height },
      };
      return;
    }

    if (kind === 'reference-move') {
      const overlay = getReferenceImageSession();

      if (!overlay) {
        return;
      }

      this.dragActive = true;
      this.referenceDrag = {
        kind: 'move',
        start: { x: overlay.x, y: overlay.y, width: overlay.width, height: overlay.height },
        startPoint: this.worldPoint(pointer),
      };
      return;
    }

    // Transform gizmos sit on an already-selected group, so they never change selection.
    if (kind === 'group-resize') {
      this.dragActive = true;
      this.beginEdit();

      const rect = this.groupScaleRect();
      const corner = this.groupHandleCorner(gameObject);

      if (rect && corner) {
        this.groupScale = { origin: gizmoCornerPoint(rect, gizmoOppositeCorner(corner)) };
      }

      return;
    }

    if (kind === 'group-rotate') {
      this.dragActive = true;
      this.beginEdit();

      const frame = this.selectionFrameRect();

      if (frame) {
        const origin = {
          x: (frame.minX + frame.maxX) / 2,
          y: (frame.minY + frame.maxY) / 2,
        };
        this.groupRotate = {
          origin,
          startAngle: toAngle(sub(this.worldPoint(pointer), origin)),
          appliedRad: 0,
        };
      }

      return;
    }

    if (additive && kind === 'flag') {
      toggleCheckpointFlag(
        this.session,
        gameObject.getData('checkpointId') as string,
        gameObject.getData('flag') as CheckpointFlag,
      );
      this.redraw();
      return;
    }

    if (kind === 'vertex') {
      const owner: RingOwner = {
        kind: gameObject.getData('ownerKind') as RingOwnerKind,
        id: gameObject.getData('ownerId') as string,
      };
      const index = gameObject.getData('index') as number;
      this.selectRingVertex(owner, index);
    } else if (kind === 'route-vertex') {
      selectRoute(this.session, gameObject.getData('ownerId') as string, gameObject.getData('index') as number);
    } else if (kind === 'bend') {
      const owner: RingOwner = {
        kind: gameObject.getData('ownerKind') as RingOwnerKind,
        id: gameObject.getData('ownerId') as string,
      };
      const edgeIndex = gameObject.getData('edgeIndex') as number;
      this.selectRingEdge(owner, edgeIndex);
    } else if (kind === 'flag') {
      const checkpointId = gameObject.getData('checkpointId') as string;
      const flag = gameObject.getData('flag') as CheckpointFlag;
      const ref = { kind: 'checkpoint', id: checkpointId } as const;

      if (!hasRef(this.session.selection, ref)) {
        selectCheckpoint(this.session, checkpointId, flag);
      } else if (this.session.selection.refs.length === 1) {
        const current = selectedFlag(this.session.selection, ref);

        if (current !== null && current !== flag) {
          selectCheckpoint(this.session, checkpointId, flag);
        }
      }

      this.flagDrag = {
        checkpointId,
        flag,
        moveGate:
          this.session.selection.refs.length > 1 ||
          selectedFlag(this.session.selection, ref) === null,
      };
    } else {
      return;
    }

    this.dragActive = true;
    this.beginEdit();
  }

  private onDrag(
    pointer: Phaser.Input.Pointer,
    gameObject: Phaser.GameObjects.GameObject,
    dragX: number,
    dragY: number,
  ): void {
    if (this.cameraControl.pinching || this.cameraControl.pinchConsumed || !this.dragActive) {
      return;
    }

    const kind = gameObject.getData('kind');
    const raw: Vec2 = { x: dragX, y: dragY };
    // Group gizmos snap on their own scale or angle, so they keep the unrounded pointer.
    const skipGrid =
      kind === 'group-resize' ||
      kind === 'group-rotate' ||
      kind === 'reference-resize' ||
      kind === 'reference-move';
    const point = this.session.snapToGrid && !skipGrid ? snapPoint(raw, SNAP_GRID) : raw;

    if (kind === 'reference-resize' || kind === 'reference-move') {
      this.dragReference(point);
      return;
    }

    if (kind === 'group-resize') {
      this.dragGroupScale(point, gameObject, (pointer.event as PointerEvent | undefined)?.shiftKey === true);
      return;
    }

    if (kind === 'group-rotate') {
      this.dragGroupRotate(point);
      return;
    }

    if (kind === 'vertex') {
      const owner: RingOwner = {
        kind: gameObject.getData('ownerKind') as RingOwnerKind,
        id: gameObject.getData('ownerId') as string,
      };
      const index = gameObject.getData('index') as number;

      if (!this.moveRingVertexFor(owner, index, point)) {
        return;
      }

      const ring = this.getRingFor(owner);
      const vertex = ring?.vertices[index];

      if (vertex && gameObject instanceof Phaser.GameObjects.Arc) {
        gameObject.setPosition(vertex.x, vertex.y);
      }

      this.requestDragPaint(() => this.redrawTerrainLayer(owner.kind));
      return;
    }

    if (kind === 'route-vertex') {
      const routeId = gameObject.getData('ownerId') as string;
      const index = gameObject.getData('index') as number;

      if (!moveRouteVertex(this.map, routeId, index, point)) {
        return;
      }

      const vertex = findRoute(this.map, routeId)?.points[index];

      if (vertex && gameObject instanceof Phaser.GameObjects.Arc) {
        gameObject.setPosition(vertex.x, vertex.y);
      }

      this.requestDragPaint(() => this.redrawRoutes());
      return;
    }

    if (kind === 'bend') {
      const owner: RingOwner = {
        kind: gameObject.getData('ownerKind') as RingOwnerKind,
        id: gameObject.getData('ownerId') as string,
      };
      const edgeIndex = gameObject.getData('edgeIndex') as number;

      if (!this.moveRingBendFor(owner, edgeIndex, point)) {
        return;
      }

      const ring = this.getRingFor(owner);
      const bend = ring?.edges[edgeIndex]?.bend;

      if (bend && gameObject instanceof Phaser.GameObjects.Arc) {
        gameObject.setPosition(bend.x, bend.y);
      }

      this.requestDragPaint(() => this.redrawTerrainLayer(owner.kind));
      return;
    }

    if (kind === 'flag') {
      const checkpointId = gameObject.getData('checkpointId') as string;
      const flag = gameObject.getData('flag') as CheckpointFlag;
      const checkpoint = findCheckpoint(this.map, checkpointId);
      const flagPoint = checkpoint?.[flag];

      if (!checkpoint || !flagPoint) {
        return;
      }

      const moveWholeGate =
        this.flagDrag?.checkpointId === checkpointId
          ? this.flagDrag.moveGate
          : this.session.selection.refs.length > 1 ||
            selectedFlag(this.session.selection, { kind: 'checkpoint', id: checkpointId }) === null;

      if (moveWholeGate) {
        moveSelection(this.session, this.map, {
          x: point.x - flagPoint.x,
          y: point.y - flagPoint.y,
        });
        this.requestDragPaint();
        return;
      }

      if (!moveFlag(this.map, checkpointId, flag, point)) {
        return;
      }

      const moved = findCheckpoint(this.map, checkpointId)?.[flag];

      if (moved && gameObject instanceof Phaser.GameObjects.Zone) {
        gameObject.setPosition(moved.x, moved.y);
      }

      this.requestDragPaint(() => this.redrawCheckpoints());
    }
  }

  private onDragEnd(): void {
    if (!this.dragActive) {
      return;
    }

    const referenceDrag = this.referenceDrag !== null;
    this.dragActive = false;
    this.flagDrag = null;
    this.groupScale = null;
    this.groupRotate = null;
    this.referenceDrag = null;

    if (referenceDrag) {
      this.dragPaint.cancel();
      this.rebuildHandles();
      return;
    }

    this.commitEdit();
    // The drag frames skipped validation and the handle rebuild; the full pass does both
    // and repaints from the final state, so the queued live paint is dropped.
    this.redraw();
  }

  // --- Ring owner dispatch -------------------------------------------------

  /** Vertex and bend handles only appear for a lone selected ring, never for a multi-selection. */
  private ringOwnerFromSelection(): RingOwner | null {
    const ref = soleRef(this.session.selection);

    if (!ref) {
      return null;
    }

    if (ref.kind === 'island' || ref.kind === 'rock' || ref.kind === 'water') {
      return { kind: ref.kind, id: ref.id };
    }

    if (ref.kind === 'startArea') {
      return { kind: 'startArea', id: '' };
    }

    return ref.kind === 'parkArea' ? { kind: 'parkArea', id: '' } : null;
  }

  private getRingFor(owner: RingOwner): Ring | undefined {
    if (owner.kind === 'island') {
      return findIsland(this.map, owner.id);
    }

    if (owner.kind === 'rock') {
      return findRock(this.map, owner.id);
    }

    if (owner.kind === 'water') {
      return findShallowWater(this.map, owner.id);
    }

    if (owner.kind === 'startArea') {
      return this.map.startArea ?? undefined;
    }

    return this.map.parkArea ?? undefined;
  }

  private findRingEdgeFor(owner: RingOwner, point: Vec2, radius: number): number | null {
    if (owner.kind === 'island') {
      return findIslandEdgeAt(this.map, owner.id, point, radius);
    }

    if (owner.kind === 'rock') {
      return findRockEdgeAt(this.map, owner.id, point, radius);
    }

    if (owner.kind === 'water') {
      return findShallowWaterEdgeAt(this.map, owner.id, point, radius);
    }

    if (owner.kind === 'startArea') {
      return findStartAreaEdgeAt(this.map, point, radius);
    }

    return findParkAreaEdgeAt(this.map, point, radius);
  }

  private moveRingBendFor(owner: RingOwner, edgeIndex: number, point: Vec2): boolean {
    if (owner.kind === 'island') {
      return moveIslandBend(this.map, owner.id, edgeIndex, point);
    }

    if (owner.kind === 'rock') {
      return moveRockBend(this.map, owner.id, edgeIndex, point);
    }

    if (owner.kind === 'water') {
      return moveShallowWaterBend(this.map, owner.id, edgeIndex, point);
    }

    if (owner.kind === 'startArea') {
      return moveStartAreaBend(this.map, edgeIndex, point);
    }

    return moveParkAreaBend(this.map, edgeIndex, point);
  }

  private moveRingVertexFor(owner: RingOwner, index: number, point: Vec2): boolean {
    if (owner.kind === 'island') {
      return moveVertex(this.map, owner.id, index, point);
    }

    if (owner.kind === 'rock') {
      return moveRockVertex(this.map, owner.id, index, point);
    }

    if (owner.kind === 'water') {
      return moveShallowWaterVertex(this.map, owner.id, index, point);
    }

    if (owner.kind === 'startArea') {
      return moveStartAreaVertex(this.map, index, point);
    }

    return moveParkAreaVertex(this.map, index, point);
  }

  private selectRingVertex(owner: RingOwner, index: number): void {
    if (owner.kind === 'island') {
      selectIsland(this.session, owner.id, index);
    } else if (owner.kind === 'rock') {
      selectRock(this.session, owner.id, index);
    } else if (owner.kind === 'water') {
      selectWater(this.session, owner.id, index);
    } else if (owner.kind === 'startArea') {
      selectStartArea(this.session, index);
    } else {
      selectParkArea(this.session, index);
    }
  }

  private selectRingEdge(owner: RingOwner, edgeIndex: number): void {
    if (owner.kind === 'island') {
      selectIsland(this.session, owner.id, null, edgeIndex);
    } else if (owner.kind === 'rock') {
      selectRock(this.session, owner.id, null, edgeIndex);
    } else if (owner.kind === 'water') {
      selectWater(this.session, owner.id, null, edgeIndex);
    } else if (owner.kind === 'startArea') {
      selectStartArea(this.session, null, edgeIndex);
    } else {
      selectParkArea(this.session, null, edgeIndex);
    }
  }

  private isRingSelected(kind: RingOwnerKind, id: string): boolean {
    return hasRef(
      this.session.selection,
      kind === 'startArea' || kind === 'parkArea' ? { kind } : { kind, id },
    );
  }

  private redrawTerrainLayer(kind: RingOwnerKind): void {
    if (kind === 'startArea') {
      this.redrawStartArea();
      return;
    }

    if (kind === 'parkArea') {
      this.redrawParkArea();
      return;
    }

    this.redrawTerrain();
  }

  // --- Inspector field mutators --------------------------------------------
  //
  // Each of these applies to every selected object of the relevant kind, so editing a field with
  // five islands selected recolours all five. Kinds the field does not apply to are skipped.

  private selectedIslandIds(): string[] {
    return selectedIds(this.session.selection, 'island');
  }

  private selectedRockIds(): string[] {
    return selectedIds(this.session.selection, 'rock');
  }

  private selectedWaterIds(): string[] {
    return selectedIds(this.session.selection, 'water');
  }

  private selectedCheckpointIds(): string[] {
    return selectedIds(this.session.selection, 'checkpoint');
  }

  private startAreaSelected(): boolean {
    return hasRef(this.session.selection, { kind: 'startArea' });
  }

  private parkAreaSelected(): boolean {
    return hasRef(this.session.selection, { kind: 'parkArea' });
  }

  private setSelectedCornerRadius(radius: number): void {
    for (const id of this.selectedIslandIds()) {
      setIslandCornerRadius(this.map, id, radius);
    }

    for (const id of this.selectedRockIds()) {
      setRockCornerRadius(this.map, id, radius);
    }

    for (const id of this.selectedWaterIds()) {
      setShallowWaterCornerRadius(this.map, id, radius);
    }

    if (this.startAreaSelected()) {
      setStartAreaCornerRadius(this.map, radius);
    }

    if (this.parkAreaSelected()) {
      setParkAreaCornerRadius(this.map, radius);
    }
  }

  /** Elevation applies to islands and rocks; shallow water uses depth instead. */
  private setSelectedElevation(elevation: number): void {
    for (const id of this.selectedIslandIds()) {
      setIslandElevation(this.map, id, elevation);
    }

    for (const id of this.selectedRockIds()) {
      setRockElevation(this.map, id, elevation);
    }
  }

  private setSelectedSkirtM(skirtM: number): void {
    for (const id of this.selectedIslandIds()) {
      setIslandSkirtM(this.map, id, skirtM);
    }
  }

  private scatterSelectedProps(): void {
    if (this.showElevation) {
      return;
    }

    for (const id of this.selectedIslandIds()) {
      scatterIsland(this.map, id, [this.session.draftProp]);
    }
  }

  private clearSelectedProps(): void {
    for (const id of this.selectedIslandIds()) {
      clearIslandDecorations(this.map, id);
    }
  }

  private setSelectedDepth(depth: number): void {
    for (const id of this.selectedWaterIds()) {
      setShallowWaterDepth(this.map, id, depth);
    }
  }

  private straightenSelectedEdge(): void {
    const ref = soleRef(this.session.selection);

    if (!ref) {
      return;
    }

    const edgeIndex = selectedEdgeIndex(this.session.selection, ref);

    if (edgeIndex === null) {
      return;
    }

    if (ref.kind === 'island') {
      clearIslandEdgeBend(this.map, ref.id, edgeIndex);
      selectIsland(this.session, ref.id);
    } else if (ref.kind === 'rock') {
      clearRockEdgeBend(this.map, ref.id, edgeIndex);
      selectRock(this.session, ref.id);
    } else if (ref.kind === 'water') {
      clearShallowWaterEdgeBend(this.map, ref.id, edgeIndex);
      selectWater(this.session, ref.id);
    } else if (ref.kind === 'startArea') {
      clearStartAreaEdgeBend(this.map, edgeIndex);
      selectStartArea(this.session);
    } else if (ref.kind === 'parkArea') {
      clearParkAreaEdgeBend(this.map, edgeIndex);
      selectParkArea(this.session);
    }
  }

  private setSelectedMaterial(material: WaterMaterial): void {
    for (const id of this.selectedWaterIds()) {
      setShallowWaterMaterial(this.map, id, material);
    }
  }

  /** The palette group offered differs per kind, but one colour applies across the selection. */
  private applyColorLive(color: Rgba): void {
    if (!this.colorEditing) {
      this.beginEdit();
      this.colorEditing = true;
    }

    this.setSelectedColor(color);
    this.redraw();
    this.syncLighting();
  }

  private commitColorEdit(): void {
    if (!this.colorEditing) {
      return;
    }

    this.colorEditing = false;
    this.commitEdit();
    this.redraw();
    this.syncLighting();
  }

  private cancelColorEdit(): void {
    if (!this.colorEditing) {
      return;
    }

    this.colorEditing = false;
    abandonLast(this.history);
    this.toolbar?.sync();
  }

  private setSelectedColor(color: Rgba): void {
    for (const id of this.selectedIslandIds()) {
      setIslandColor(this.map, id, color);
    }

    for (const id of this.selectedRockIds()) {
      setRockColor(this.map, id, color);
    }

    for (const id of this.selectedWaterIds()) {
      setShallowWaterColor(this.map, id, color);
    }

    if (this.startAreaSelected()) {
      setStartAreaColor(this.map, color);
    }

    if (this.parkAreaSelected()) {
      setParkAreaColor(this.map, color);
    }

    for (const id of selectedIds(this.session.selection, 'route')) {
      setRouteColor(this.map, id, color);
    }
  }

  private setSelectedCheckpointOrder(order: number | null): void {
    for (const id of this.selectedCheckpointIds()) {
      setCheckpointOrder(this.map, id, order);
    }
  }

  private setSelectedCheckpointGoal(goal: boolean): void {
    for (const id of this.selectedCheckpointIds()) {
      setCheckpointGoal(this.map, id, goal);
    }
  }

  private setSelectedGateWidth(width: number): void {
    for (const id of this.selectedCheckpointIds()) {
      setGateWidth(this.map, id, width);
    }
  }

  // --- History / persistence ----------------------------------------------

  private mutate(fn: () => unknown): void {
    this.beginEdit();
    fn();
    this.commitEdit();
    this.redraw();
    this.syncLighting();
  }

  private beginEdit(): void {
    pushUndo(this.history, this.map);
  }

  private commitEdit(): void {
    reconcileSelection(this.session, this.map);
    this.touchMap();
    this.persistWorkingCopy();
    this.toolbar?.sync();
  }

  private rememberBaseline(): void {
    this.baselineJson = serializeMap(this.map);
  }

  /** Writes a crash-recovery draft only while the working copy differs from the last save/load. */
  private persistWorkingCopy(): void {
    if (this.draftOfferPending) {
      return;
    }

    if (serializeMap(this.map) === this.baselineJson) {
      const draft = loadResumableDraft();

      if (draft && draft.fileName === this.repoFile) {
        clearResumableDraft();
      }

      return;
    }

    saveResumableDraft(this.map);
    saveRepoFileName(this.repoFile);
  }

  private markClean(): void {
    this.rememberBaseline();
    clearResumableDraft();
  }

  private readonly onWindowBeforeUnload = (): void => {
    this.persistWorkingCopy();
    this.disposeReferenceImage();
    clearReferenceImageSession();
  };

  private touchMap(): void {
    this.mapRevision += 1;
  }

  /** Drops in-progress gestures without touching the selection. */
  private resetTransientState(): void {
    this.dragPaint.cancel();
    cancelDraft(this.session);
    cancelCheckpointDraft(this.session);
    this.hover = null;
    this.activeBendDrag = null;
    this.marquee = null;
    this.groupDrag = null;
    this.groupScale = null;
    this.groupRotate = null;
    this.referenceDrag = null;
    this.flagDrag = null;
    this.minimapDrag = false;
  }

  private resetSessionState(): void {
    clearSelection(this.session);
    this.resetTransientState();
  }

  /** Undo and redo keep whatever is still selected, rather than dropping the selection entirely. */
  private restoreMap(next: MapDocument): void {
    this.map = next;
    this.columnVisibleKey = '';
    this.columnVisibleIds = null;
    this.sunEditing = false;
    this.lookEditing = false;
    this.colorEditing = false;
    this.resetTransientState();
    reconcileSelection(this.session, this.map);
    this.touchMap();
    this.persistWorkingCopy();
    this.redraw();
    this.syncLighting();
    this.toolbar?.sync();
  }

  private handleUndo(): void {
    const previous = undo(this.history, this.map);

    if (previous) {
      this.restoreMap(previous);
    }
  }

  private handleRedo(): void {
    const next = redo(this.history, this.map);

    if (next) {
      this.restoreMap(next);
    }
  }

  private currentFileName(): string {
    return mapFileDisplayName(this.fileHandle, this.repoFile ?? this.importedName);
  }

  private bindFile(handle: FileSystemFileHandle | null, repoFile: string | null = null): void {
    this.fileHandle = handle;
    this.repoFile = handle?.name ?? repoFile;
    this.importedName = null;
    saveRepoFileName(this.repoFile);
    this.pushValidation();
    this.toolbar?.sync();
  }

  private bindImported(fileName: string): void {
    this.fileHandle = null;
    this.repoFile = null;
    this.importedName = fileName;
    saveRepoFileName(null);
    this.pushValidation();
    this.toolbar?.sync();
  }

  private isDirty(): boolean {
    return serializeMap(this.map) !== this.baselineJson;
  }

  private async confirmDiscardIfDirty(): Promise<boolean> {
    if (!this.isDirty()) {
      return true;
    }

    return confirmDialog({
      title: 'Discard unsaved changes?',
      message: 'Opening another map will replace your current work.',
      confirmLabel: 'Discard',
      cancelLabel: 'Cancel',
    });
  }

  private applyOpenedMap(
    map: MapDocument,
    binding: { repoFile?: string | null; importedName?: string; message?: string },
  ): void {
    if (binding.importedName) {
      this.bindImported(binding.importedName);
    } else {
      this.bindFile(null, binding.repoFile ?? null);
    }

    this.mutate(() => {
      this.map = map;
      this.columnVisibleKey = '';
      this.columnVisibleIds = null;
      this.resetSessionState();
      this.applyPersistedRouteVisibility();
    });
    this.markClean();
    this.applyMapBounds();
    this.rememberRecentMap();
    this.toolbar?.showMessage(binding.message ?? 'Map opened.', 'success');
  }

  private handleNew(): void {
    this.bindFile(null);
    this.mutate(() => {
      this.map = createEmptyMap();
      this.columnVisibleKey = '';
      this.columnVisibleIds = null;
      this.resetSessionState();
      this.applyPersistedRouteVisibility();
    });
    this.markClean();
    this.applyMapBounds();
  }

  private handleEscape(): boolean {
    if (this.pauseMenu) {
      this.closePause();
      return true;
    }

    if (this.consumeEditorEscape()) {
      return true;
    }

    this.openPause();
    return true;
  }

  /** One in-progress edit per key: a draft or bend drag, then a selection. */
  private consumeEditorEscape(): boolean {
    const drafting =
      this.session.draft.length > 0 ||
      this.session.checkpointDraft !== null ||
      this.activeBendDrag !== null;

    if (drafting) {
      cancelDraft(this.session);
      cancelCheckpointDraft(this.session);
      this.activeBendDrag = null;
      this.hover = null;
      this.redraw();
      return true;
    }

    if (this.session.selection.refs.length > 0) {
      clearSelection(this.session);
      this.hover = null;
      this.redraw();
      return true;
    }

    return false;
  }

  private openPause(): void {
    if (this.pauseMenu || this.leaving) {
      return;
    }

    this.scene.pause();
    this.pauseMenu = mountPauseMenu({
      scene: 'editor',
      onResume: () => this.closePause(),
      onEditor: () => undefined,
      onBoats: () => {
        void this.leaveViaPause('boats');
      },
      onMainMenu: () => {
        void this.leaveViaPause('start');
      },
    });
  }

  private closePause(): void {
    this.destroyPause();

    if (this.scene.isPaused()) {
      this.scene.resume();
    }
  }

  private destroyPause(): void {
    this.pauseMenu?.destroy();
    this.pauseMenu = null;
  }

  private sceneLive(): boolean {
    return this.sys.isActive() || this.sys.isPaused();
  }

  private async leaveViaPause(next: 'start' | 'boats'): Promise<void> {
    if (this.leaving || !this.sceneLive()) {
      return;
    }

    if (this.isDirty()) {
      const discard = await confirmDialog({
        title: 'Discard unsaved changes?',
        message: 'Your unsaved changes will be discarded.',
        confirmLabel: 'Discard',
        cancelLabel: 'Cancel',
      });

      if (!discard || !this.sceneLive()) {
        return;
      }

      this.markClean();
    }

    if (this.leaving || !this.sceneLive()) {
      return;
    }

    this.leaving = true;
    this.minimap?.destroy();
    this.minimap = null;
    clearSceneSearch();

    if (this.scene.isPaused()) {
      this.scene.resume();
    }

    if (next === 'boats') {
      this.scene.start('BoatScene', {});
      return;
    }

    this.scene.start('StartScene');
  }

  private handleSave(): void {
    void this.saveWithDialog();
  }

  private handleSaveAs(): void {
    void this.saveWithDialog({ saveAs: true });
  }

  private handleSaveAndClose(): void {
    void this.saveAndClose();
  }

  /**
   * Races a clone of the working document. Map → Test drive and the tool-rail button are
   * disabled until `validateMap` reports no errors (warnings still allow a race). The race
   * starts in placement: click open water anywhere, then click to set heading. Escape or
   * Back to editor starts this scene again with that clone, so the same map comes back. The
   * autosave is written first so the work survives even if the round trip does not.
   */
  private async refreshTestBoats(): Promise<void> {
    const catalog = await loadBoatCatalog();

    if (!this.sceneLive()) {
      return;
    }

    this.testBoats = boatTestChoices(catalog);
    this.toolbar?.sync();
  }

  private async handleTestBoat(id: string): Promise<void> {
    if (!this.playable || !this.sceneLive()) {
      return;
    }

    const catalog = await loadBoatCatalog();

    if (!this.sceneLive() || !this.playable) {
      return;
    }

    const boat = resolveTestBoat(id, catalog);

    if (!boat) {
      this.toolbar?.showMessage('That boat is no longer in the catalog.', 'error');
      void this.refreshTestBoats();
      return;
    }

    this.persistWorkingCopy();
    // Remove the extra camera while the manager is still alive; plugin SHUTDOWN destroys
    // cameras first, and a later remove of that dead camera is easy to get wrong.
    this.minimap?.destroy();
    this.minimap = null;
    this.scene.start('GameScene', handoffFromEditor(this.map, boat, this.repoFile));
  }

  /** Jumps to an object named by a validation issue: selects it and centres the camera on it. */
  private revealRef(ref: ObjectRef): void {
    selectOnly(this.session, ref);

    const bounds = refBounds(this.map, ref);

    if (bounds) {
      this.cameras.main.centerOn(
        (bounds.minX + bounds.maxX) / 2,
        (bounds.minY + bounds.maxY) / 2,
      );
      this.constrainCamera();
    }

    this.redraw();
    this.game.canvas.focus();
  }

  private async saveWithDialog(options: { saveAs?: boolean } = {}): Promise<SaveMapResult> {
    const saveAs = options.saveAs === true;
    const unbound = this.repoFile === null && this.fileHandle === null;
    let firstSaveName: string | null = null;

    if (unbound && !saveAs) {
      const suggested = suggestMapFilename(this.map.name) ?? 'battleboats-map';
      const draft = await promptMapSave({
        filename: suggested,
        meta: this.map.meta,
        askFilename: canWriteRepoMap(),
      });

      if (!draft) {
        return { status: 'cancelled' };
      }

      const nextMeta = sanitizeMapMeta(draft.meta);

      if (!mapMetaEquals(this.map.meta, nextMeta)) {
        this.mutate(() => setMapMeta(this.map, nextMeta));
      }

      firstSaveName = draft.filename;
    }

    const result = await saveMap({
      map: this.map,
      handle: this.fileHandle,
      repoFile: this.repoFile,
      saveAs,
      pickRepoFile: (suggested) =>
        firstSaveName !== null
          ? Promise.resolve(firstSaveName)
          : promptMapFilename(this.repoFile ?? suggestMapFilename(this.map.name) ?? suggested),
    });

    if (result.status === 'saved') {
      this.bindFile(result.handle, result.repoFile);
      this.markClean();
      if (result.repoFile) {
        this.rememberRecentMap();
      }
      this.toolbar?.showMessage('Map saved.', 'success');
    } else if (result.status === 'error') {
      this.toolbar?.showMessage('Could not save the map.', 'error');
    }

    return result;
  }

  private async saveAndClose(): Promise<void> {
    const result = await this.saveWithDialog();

    if (result.status !== 'saved' || !this.sys.isActive()) {
      return;
    }

    this.minimap?.destroy();
    this.minimap = null;

    clearSceneSearch();
    this.scene.start('StartScene');
  }

  private handleExport(): void {
    exportMapFile(this.map, suggestMapFilename(this.map.name) ?? undefined);
    this.toolbar?.showMessage('Map exported.', 'success');
  }

  private handleOpen(): void {
    void this.openFromCatalog();
  }

  private rememberRecentMap(): void {
    if (!this.repoFile) {
      return;
    }

    const file = sanitizeMapFilename(this.repoFile);

    if (!file) {
      return;
    }

    const id = file.replace(/\.json$/i, '');

    if (!id || id === 'Untitled') {
      return;
    }

    const label = this.map.name.trim() || labelFromFilename(id);
    this.recentMaps = recordRecentMap(id, label);
    this.toolbar?.sync();
  }

  private async openRecentMap(id: string): Promise<void> {
    const catalog = await loadMapCatalog();

    if (!this.sys.isActive()) {
      return;
    }

    const entry = findMapCatalogEntry(id, catalog);

    if (!entry) {
      this.recentMaps = dropRecentMap(id);
      this.toolbar?.sync();
      this.toolbar?.showMessage('That map is no longer in the catalog.', 'error');
      return;
    }

    if (!(await this.confirmDiscardIfDirty()) || !this.sys.isActive()) {
      return;
    }

    this.applyOpenedMap(cloneMapDocument(entry.map), { repoFile: `${entry.id}.json` });
  }

  private currentMapLabel(): string {
    return mapDisplayName(
      this.map.name,
      labelFromFilename(this.repoFile ?? this.importedName ?? '') || UNTITLED_MAP_NAME,
    );
  }

  private handleDeleteMap(): void {
    void this.deleteCurrentMap();
  }

  /**
   * DEV-only: unlink the bound `maps/<name>.json`, drop it from recents, and start a blank map.
   * Untitled and imported documents have nothing on disk to delete.
   */
  private async deleteCurrentMap(): Promise<void> {
    if (!canDeleteRepoMap(this.repoFile)) {
      return;
    }

    const file = sanitizeMapFilename(this.repoFile ?? '');

    if (!file) {
      return;
    }

    const confirmed = await confirmDialog({
      title: 'Are you sure you want to delete the map?',
      detail: this.currentMapLabel(),
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });

    if (!confirmed || !this.sys.isActive()) {
      return;
    }

    const deleted = await deleteRepoMap(file);

    if (!deleted) {
      this.toolbar?.showMessage('Could not delete the map.', 'error');
      return;
    }

    const id = file.replace(/\.json$/i, '');
    this.recentMaps = dropRecentMap(id);
    this.persistEditorSettings();
    this.handleNew();
    this.toolbar?.showMessage('Map deleted.', 'success');
  }

  private handleImport(): void {
    void this.importFromFile();
  }

  private async importFromFile(): Promise<void> {
    const picked = await pickMapJsonFile();

    if (!this.sys.isActive()) {
      return;
    }

    if (picked.status === 'cancelled') {
      return;
    }

    if (picked.status === 'unreadable') {
      this.toolbar?.showMessage('Could not read that map file.', 'error');
      return;
    }

    await this.applyImportedJson(picked.json, picked.fileName);
  }

  private async applyImportedJson(json: string, fileName: string): Promise<void> {
    let result = importMapDocument(json);

    if (!result.ok) {
      const accepted = await reportMapImportFailure(result);

      if (!accepted || !result.fix || !this.sys.isActive()) {
        return;
      }

      result = applyMapImportFix(json, result.fix.id);

      if (!result.ok) {
        await reportMapImportFailure(result);
        return;
      }
    }

    if (!(await this.confirmDiscardIfDirty()) || !this.sys.isActive()) {
      return;
    }

    this.applyOpenedMap(result.map, {
      importedName: fileName,
      message: importSuccessMessage(result.notes),
    });
  }

  private async openFromCatalog(): Promise<void> {
    const catalog = await loadMapCatalog();

    if (!this.sys.isActive()) {
      return;
    }

    const choice = await promptOpenMap(
      catalog.map((entry) => ({ id: entry.id, label: entry.label })),
      { onError: (message) => this.toolbar?.showMessage(message, 'error') },
    );

    if (!choice || !this.sys.isActive()) {
      return;
    }

    if (!(await this.confirmDiscardIfDirty()) || !this.sys.isActive()) {
      return;
    }

    if (choice.kind === 'import') {
      this.applyOpenedMap(choice.map, {
        importedName: choice.fileName,
        message: importSuccessMessage(choice.notes),
      });
      return;
    }

    const entry = findMapCatalogEntry(choice.id, catalog);

    if (!entry) {
      this.toolbar?.showMessage('Could not open that map file.', 'error');
      return;
    }

    this.applyOpenedMap(cloneMapDocument(entry.map), { repoFile: `${entry.id}.json` });
  }

  // --- Rendering -----------------------------------------------------------

  private currentLogBadge(): EditorLogBadge | null {
    if (this.lastErrorCount > 0) {
      return 'error';
    }

    return this.lastWarningCount > 0 ? 'warning' : null;
  }

  private pushValidation(): void {
    const validation = validateMap(this.map);
    const next = appendValidationLog(this.logEntries, this.logSeenIssues, validation.issues);
    this.logEntries = next.entries;
    this.playable = validation.playable;
    this.lastErrorCount = validation.errorCount;
    this.lastWarningCount = validation.warningCount;

    if (next.added > 0) {
      this.logAdded += 1;
      this.logFlashEntries = next.entries.slice(next.entries.length - next.added);
    }

    this.validation?.sync();
  }

  private redraw(options: { rebuildHandles?: boolean } = {}): void {
    this.dragPaint.cancel();
    this.redrawTerrain();
    this.redrawAuthoringChrome(options);
    this.pushValidation();
    this.toolbar?.sync();
  }

  /**
   * One frame of a live gesture: paint what moved and slide the handles along with it.
   *
   * Everything a drag does not change waits for the commit. `validateMap` walks the whole
   * document, `rebuildHandles` destroys and re-registers an interactive Arc per vertex, and
   * `toolbar.sync` rebuilds a thirty-odd field state object for a deep compare — none of
   * that belongs on a pointer move (docs/PERFORMANCE.md).
   */
  private redrawLive(): void {
    this.redrawTerrain();
    this.redrawStartArea();
    this.redrawParkArea();
    this.redrawCheckpoints();
    this.redrawRoutes();
    this.redrawDraft();
    this.redrawOverlay();
    this.syncHandlePositions();
    this.syncSelectionHandleAttr();
    this.syncSelectionGizmoAttr();
  }

  /** Queue `redrawLive` for this frame. Repeated calls in one frame paint once. */
  private requestDragPaint(paint: () => void = () => this.redrawLive()): void {
    this.dragPaint.request(paint);
  }

  /** Handles, labels, gates, and draft — not column fills. */
  private redrawAuthoringChrome(options: { rebuildHandles?: boolean } = {}): void {
    this.redrawStartArea();
    this.redrawParkArea();
    this.redrawCheckpoints();
    this.redrawRoutes();
    this.redrawDraft();
    this.redrawOverlay();
    this.syncReferenceImage();

    if (options.rebuildHandles !== false) {
      this.rebuildHandles();
    }

    this.syncSelectionHandleAttr();
    this.syncSelectionGizmoAttr();
    this.syncMinimap();
  }

  /**
   * The minimap shares the world layers, so the only per-redraw work is hiding the chrome that
   * would be noise at map scale. Handles and labels are recreated constantly, hence the re-hide.
   */
  private syncMinimap(): void {
    const minimap = this.minimap;

    if (!minimap) {
      return;
    }

    const hidden: Phaser.GameObjects.GameObject[] = [
      this.gridGraphics,
      this.draftGraphics,
      this.routeGraphics,
      this.overlayGraphics,
      this.checkpointSubmergedGraphics,
      this.checkpointWaterlineGraphics,
      this.checkpointGraphics,
      ...(this.checkpointPoles?.graphics() ?? []),
      ...this.handles,
    ];

    if (this.referenceImage) {
      hidden.push(this.referenceImage);
    }

    if (this.boatGuide) {
      hidden.push(this.boatGuide);
    }

    if (this.startAreaLabel) {
      hidden.push(this.startAreaLabel);
    }

    if (this.parkAreaLabel) {
      hidden.push(this.parkAreaLabel);
    }

    for (const label of this.checkpointLabels.values()) {
      hidden.push(label);
    }

    for (const label of this.routeLabels.values()) {
      hidden.push(label);
    }

    for (const flags of this.checkpointFlagHandles.values()) {
      hidden.push(flags.a, flags.b);
    }

    minimap.hide(hidden);
    minimap.draw(this.map, {
      hiddenRouteIds: this.hiddenRouteIds,
      selectedRouteIds: new Set(selectedIds(this.session.selection, 'route')),
    });
  }

  /** The marquee rectangle and the selection frame when gizmos are showing. */
  private redrawOverlay(): void {
    const gfx = this.overlayGraphics;
    gfx.clear();

    if (this.marquee) {
      const rect = normalizeRect(this.marquee.start, this.marquee.current);
      gfx.fillStyle(SELECTED_HIGHLIGHT, 0.12);
      gfx.fillRect(rect.minX, rect.minY, rect.maxX - rect.minX, rect.maxY - rect.minY);
      gfx.lineStyle(this.worldRadius(1), SELECTED_HIGHLIGHT, 0.9);
      gfx.strokeRect(rect.minX, rect.minY, rect.maxX - rect.minX, rect.maxY - rect.minY);
    }

    const frame = this.selectionFrameRect();

    if (!frame || this.selectionHandlesHidden() || this.currentGizmoMode() === 'none') {
      return;
    }

    gfx.lineStyle(this.worldRadius(1), SELECTED_HIGHLIGHT, 0.7);
    gfx.strokeRect(
      frame.minX,
      frame.minY,
      frame.maxX - frame.minX,
      frame.maxY - frame.minY,
    );
  }

  private terrainGraphics(): TerrainGraphics {
    return {
      water: this.waterGraphics,
      waterChrome: this.waterChromeGraphics,
      skirt: this.skirtGraphics,
      land: this.landGraphics,
      rock: this.rockGraphics,
      waterlineHint: this.waterlineHintGraphics,
    };
  }

  private redrawTerrain(paint: TerrainPaintMode = 'all'): void {
    // `applyMapBounds` runs before `create()` has rebuilt the layers, and after a scene
    // restart the old fields still point at destroyed objects (`scene` is gone).
    if (!this.landGraphics?.scene) {
      return;
    }

    const useColumn = this.columnsVisible();
    drawTerrain(this.terrainGraphics(), this.map, {
      strokeScale: this.worldRadius(1),
      waterlineHintAlpha: useColumn ? this.map.look.waterlineHintAlpha : 0,
      columns: this.elevatedLayers ?? undefined,
      extrudeColumns: useColumn,
      capFlock: this.map.look.capFlockEnabled,
      capFlockAmount: this.map.look.capFlockAmount,
      lightBeds: this.previewLighting,
      paint,
      bodyVisible: this.columnVisibleIds
        ? (id) => this.columnVisibleIds?.has(id) === true
        : undefined,
      isSelected: (kind, id) => this.isRingSelected(kind, id),
      selectedStroke: SELECTED_HIGHLIGHT,
      selectedStrokePx: RING_SELECTED_STROKE_PX,
    });

    if (paint !== 'chrome') {
      this.islandOverlays?.sync(this.map, useColumn);
    }

    if (paint === 'chrome' || paint === 'all') {
      this.mapLayersZoom = this.cameras.main.zoom;
    }

    // Terrain objects or the map itself changed; reapply the cull regardless of camera.
    this.cullOffscreenColumns(true);
  }

  private cullOffscreenColumns(force = false): void {
    if (!this.elevatedLayers && !this.islandOverlays) {
      return;
    }

    const elevate = this.columnsVisible();
    const hintAlpha = elevate ? this.map.look.waterlineHintAlpha : 0;
    const next = applyColumnVisibility({
      map: this.map,
      cam: this.cameras.main,
      elevate,
      columns: this.elevatedLayers,
      overlays: this.islandOverlays,
      lastKey: this.columnVisibleKey,
      lastIds: this.columnVisibleIds,
      lastView: this.columnVisibleView,
      force,
      restrokeHints: (ids) => {
        paintWaterlineHints(
          this.waterlineHintGraphics,
          this.map,
          this.worldRadius(1),
          hintAlpha,
          (id) => ids.has(id),
        );
      },
    });

    this.columnVisibleKey = next.key;
    this.columnVisibleIds = next.ids;
    this.columnVisibleView = next.view;
    this.lighting?.syncVisibleShade(this.map, next.ids, next.key);
  }

  private syncLighting(): void {
    this.lighting?.setEnabled(this.previewLighting, this.map, this.columnVisibleIds);
    this.applySheetLook();

    if (this.landGraphics?.scene) {
      this.redrawTerrain();
    }
  }

  private applySheetLook(): void {
    if (this.gridGraphics?.scene) {
      this.redrawGrid();
    }
  }

  /** Live water/grid drag: one undo snapshot for the gesture. */
  private applyLookLive(patch: Partial<MapLook>): void {
    if (!this.lookEditing) {
      this.beginEdit();
      this.lookEditing = true;
    }

    updateLook(this.map.look, patch);
    this.applySheetLook();
    this.toolbar?.sync();
  }

  private commitLookEdit(): void {
    this.applySheetLook();

    if (!this.lookEditing) {
      return;
    }

    this.lookEditing = false;
    this.commitEdit();
  }

  private cancelLookEdit(): void {
    if (!this.lookEditing) {
      return;
    }

    this.lookEditing = false;
    abandonLast(this.history);
    this.applySheetLook();
    this.toolbar?.sync();
  }

  private cancelSunBake(): void {
    if (this.sunBakeFrame !== null) {
      cancelAnimationFrame(this.sunBakeFrame);
      this.sunBakeFrame = null;
    }
  }

  /** Live sun drag: one undo snapshot, at most one bake per frame. */
  private applySunLive(sun: Partial<SunSettings>): void {
    if (!this.sunEditing) {
      this.beginEdit();
      this.sunEditing = true;
    }

    updateEnvironment(this.map.environment, { sun });

    if (this.sunBakeFrame !== null) {
      return;
    }

    this.sunBakeFrame = requestAnimationFrame(() => {
      this.sunBakeFrame = null;
      this.syncLighting();
    });
  }

  private commitSunEdit(): void {
    this.cancelSunBake();
    this.syncLighting();

    if (!this.sunEditing) {
      return;
    }

    this.sunEditing = false;
    this.commitEdit();
  }

  private persistEditorSettings(): void {
    patchEditorSettings({
      logVisible: this.logVisible,
      logFilter: this.logFilter,
      showScale: this.showScale,
      showBoat: this.showBoat,
      showMinimap: this.showMinimap,
      showPerf: this.showPerf,
      showRoutes: this.showRoutes,
      snapToGrid: this.session.snapToGrid,
      previewLighting: this.previewLighting,
      showElevation: this.showElevation,
      showGrid: this.showGrid,
      referenceImageAlpha: this.referenceImageAlpha,
      terraceLevels: this.session.draftTerraceLevels,
      terracePeakM: this.session.draftTerracePeakM,
      terraceRadius: this.session.draftTerraceRadius,
      terraceColorVariation: this.session.draftTerraceColorVariation,
      terraceJitter: this.session.draftTerraceJitter,
      terraceElevRand: this.session.draftTerraceElevRand,
      terraceRadiusRand: this.session.draftTerraceRadiusRand,
      recentMaps: this.recentMaps,
    });
  }

  private setScaleVisible(on: boolean): void {
    this.showScale = on;
    this.scaleMeter?.setVisible(on);
    this.persistEditorSettings();
    this.toolbar?.sync();
  }

  private setBoatGuideVisible(on: boolean): void {
    if (on === this.showBoat) {
      return;
    }

    this.showBoat = on;

    if (on && this.input.isOver) {
      const pointer = this.input.activePointer;
      this.noteBoatGuidePointer(pointer.x, pointer.y);
    }

    if (!this.boatGuideActive()) {
      this.boatGuideScreen = null;
    }

    this.syncBoatGuide();
    this.persistEditorSettings();
    this.toolbar?.sync();
  }

  private readonly onPointerLeaveCanvas = (): void => {
    this.boatGuideScreen = null;
    this.boatGuide?.setVisible(false);
  };

  /** Remember the canvas point. The minimap is a second camera, so the guide stays off it. */
  private noteBoatGuidePointer(x: number, y: number): void {
    if (!this.boatGuideActive() || this.minimap?.contains(x, y)) {
      this.boatGuideScreen = null;
      return;
    }

    this.boatGuideScreen = { x, y };
  }

  /**
   * Park the hull beside the cursor in world space. Display size stays in metres so camera
   * zoom scales it with the map; the 30 px gap is converted from the current zoom each frame
   * so a pan or wheel keeps the stern a fixed screen distance from the pointer.
   */
  private syncBoatGuide(): void {
    const guide = this.boatGuide;

    if (!guide) {
      return;
    }

    const screen = this.boatGuideScreen;
    const root = this.game.canvas.parentElement;

    if (!this.boatGuideActive() || !screen || !this.input.isOver) {
      guide.setVisible(false);
      root?.removeAttribute('data-boat-guide');
      root?.removeAttribute('data-boat-guide-heading');
      return;
    }

    const cam = this.cameras.main;
    const view = cameraWorldRect(cam.scrollX, cam.scrollY, cam.width, cam.height, cam.zoom);
    const cursorX = view.x + (screen.x - cam.x) / cam.zoom;
    const cursorY = view.y + (screen.y - cam.y) / cam.zoom;
    const center = boatGuideCenter(cursorX, cursorY, cam.zoom);
    guide.setPosition(center.x, center.y);
    guide.setRotation((this.boatGuideHeading * Math.PI) / 180);
    guide.setVisible(true);
    root?.setAttribute('data-boat-guide', 'shown');
    root?.setAttribute('data-boat-guide-heading', String(this.boatGuideHeading));
  }

  private setMinimapVisible(on: boolean): void {
    this.showMinimap = on;
    this.minimap?.setVisible(on);
    this.game.canvas.parentElement?.setAttribute('data-minimap', on ? 'true' : 'false');
    this.persistEditorSettings();
    this.toolbar?.sync();
  }

  private setPerfVisible(on: boolean): void {
    if (on === this.showPerf) {
      return;
    }

    this.showPerf = on;
    this.syncPerfPanel();
    this.persistEditorSettings();
    this.toolbar?.sync();
  }

  /** Mounts or drops the readout plate. `pushPerf` only feeds it while it is up. */
  private syncPerfPanel(): void {
    if (this.showPerf && !this.perfPanel) {
      this.perfPanel = mountEditorPerf();
      this.perfPanel.sync({ perf: this.scenePerf?.readout ?? EMPTY_PERF_READOUT });
      return;
    }

    if (!this.showPerf && this.perfPanel) {
      this.perfPanel.destroy();
      this.perfPanel = null;
    }
  }

  /**
   * Same cadence as play: the probe samples every frame, the overlay sees four summaries a
   * second. `?perf` publishes `data-perf-*` regardless of whether the plate is up.
   */
  private pushPerf(): void {
    const next = this.scenePerf?.poll();

    if (next) {
      this.perfPanel?.sync({ perf: next });
    }
  }

  private anyRouteVisible(): boolean {
    return this.map.routes.some((route) => !this.hiddenRouteIds.has(route.id));
  }

  private pruneHiddenRoutes(): void {
    for (const id of [...this.hiddenRouteIds]) {
      if (!findRoute(this.map, id)) {
        this.hiddenRouteIds.delete(id);
      }
    }
  }

  private applyPersistedRouteVisibility(): void {
    if (this.showRoutes) {
      this.hiddenRouteIds.clear();
      return;
    }

    this.hiddenRouteIds = new Set(this.map.routes.map((route) => route.id));
  }

  private deselectHiddenRoutes(): void {
    for (const id of selectedIds(this.session.selection, 'route')) {
      if (this.hiddenRouteIds.has(id)) {
        toggleSelection(this.session, { kind: 'route', id });
      }
    }
  }

  private syncShowRoutesFlag(): void {
    this.pruneHiddenRoutes();
    const any = this.map.routes.length === 0 || this.anyRouteVisible();

    this.game.canvas.parentElement?.setAttribute('data-show-routes', any ? 'true' : 'false');

    if (this.showRoutes === any) {
      return;
    }

    this.showRoutes = any;
    this.persistEditorSettings();
  }

  private toggleRouteVisibility(routeId: string): void {
    if (this.hiddenRouteIds.has(routeId)) {
      this.hiddenRouteIds.delete(routeId);
    } else {
      this.hiddenRouteIds.add(routeId);
    }

    this.deselectHiddenRoutes();
    this.syncShowRoutesFlag();
    this.redraw();
  }

  private setRoutesVisible(on: boolean): void {
    if (on) {
      this.hiddenRouteIds.clear();
    } else {
      this.hiddenRouteIds = new Set(this.map.routes.map((route) => route.id));
      this.deselectHiddenRoutes();
    }

    this.syncShowRoutesFlag();
    this.redraw();
  }

  private redrawElevationLayers(paint: TerrainPaintMode = 'all'): void {
    this.redrawTerrain(paint);
  }

  /** 2.5-D columns follow View → Elevation only. Preview lighting does not own them. */
  private columnsVisible(): boolean {
    return this.showElevation;
  }

  private setElevationVisible(on: boolean): void {
    if (on === this.showElevation) {
      return;
    }

    this.showElevation = on;

    if (
      on &&
      (this.session.tool === 'place-prop' ||
        this.session.tool === 'scale' ||
        this.session.tool === 'rotate')
    ) {
      setTool(this.session, 'select');
      this.hover = null;
      this.syncCursor();
    }

    if (on) {
      this.session.selection = withoutRingSub(this.session.selection);
      this.groupScale = null;
      this.groupRotate = null;
    }

    this.redrawElevationLayers();
    this.rebuildHandles();
    this.persistEditorSettings();
    this.toolbar?.sync();
  }

  private setShowCapFlock(on: boolean): void {
    if (on === this.map.look.capFlockEnabled) {
      return;
    }

    this.beginEdit();
    updateLook(this.map.look, { capFlockEnabled: on });
    this.commitEdit();

    if (this.columnsVisible()) {
      this.redrawElevationLayers();
    }

    this.toolbar?.sync();
  }

  private setCapFlockAmount(next: number): void {
    const amount = clamp(next, 0, 1);

    if (amount === this.map.look.capFlockAmount) {
      return;
    }

    if (!this.lookEditing) {
      this.beginEdit();
      this.lookEditing = true;
    }

    updateLook(this.map.look, { capFlockAmount: amount });

    if (this.columnsVisible() && this.map.look.capFlockEnabled) {
      this.redrawElevationLayers();
    }

    this.toolbar?.sync();
  }

  private setShowGrid(on: boolean): void {
    if (on === this.showGrid) {
      return;
    }

    this.showGrid = on;
    this.redrawGrid();
    this.persistEditorSettings();
    this.toolbar?.sync();
  }

  private setShowReferenceImage(on: boolean): void {
    const overlay = getReferenceImageSession();

    if (!overlay || overlay.visible === on) {
      return;
    }

    patchReferenceImageSession({ visible: on });
    this.syncReferenceImage();
    this.rebuildHandles();
    this.syncMinimap();
    this.toolbar?.sync();
  }

  private setReferenceImageAlpha(next: number): void {
    const alpha = clamp(next, 0, 1);

    if (alpha === this.referenceImageAlpha) {
      return;
    }

    this.referenceImageAlpha = alpha;
    this.syncReferenceImage();
    this.persistEditorSettings();
    this.toolbar?.sync();
  }

  private handleLoadReferenceImage(): void {
    void this.loadReferenceImageFromFile();
  }

  private async loadReferenceImageFromFile(): Promise<void> {
    const picked = await pickImageFile();

    if (!this.sys.isActive()) {
      return;
    }

    if (picked.status === 'cancelled') {
      return;
    }

    if (picked.status === 'too-large') {
      this.toolbar?.showMessage('That image is too large (20 MB limit).', 'error');
      return;
    }

    const objectUrl = URL.createObjectURL(picked.file);

    try {
      const image = await loadHtmlImage(objectUrl);

      if (!this.sys.isActive()) {
        URL.revokeObjectURL(objectUrl);
        return;
      }

      this.installReferenceTexture(image);
      const fitted = fitReferenceInBounds(image.naturalWidth, image.naturalHeight, this.map.bounds);
      setReferenceImageSession({
        objectUrl,
        fileName: picked.fileName,
        sourceWidth: image.naturalWidth,
        sourceHeight: image.naturalHeight,
        ...fitted,
        visible: true,
      });
      this.spawnReferenceImage();
      this.rebuildHandles();
      this.syncMinimap();
      this.toolbar?.sync();
      this.toolbar?.showMessage('Reference image loaded.', 'success');
    } catch {
      URL.revokeObjectURL(objectUrl);

      if (this.sys.isActive()) {
        this.toolbar?.showMessage('Could not read that image.', 'error');
      }
    }
  }

  private handleClearReferenceImage(): void {
    if (!hasReferenceImage()) {
      return;
    }

    this.disposeReferenceImage();
    clearReferenceImageSession();
    this.rebuildHandles();
    this.syncMinimap();
    this.toolbar?.sync();
    this.toolbar?.showMessage('Reference image cleared.', 'success');
  }

  private hydrateReferenceImage(): void {
    const overlay = getReferenceImageSession();

    if (!overlay) {
      this.syncReferenceAttr();
      return;
    }

    if (this.textures.exists(REFERENCE_IMAGE_TEXTURE_KEY)) {
      this.spawnReferenceImage();
      return;
    }

    void this.reloadReferenceTexture(overlay);
  }

  private async reloadReferenceTexture(overlay: ReferenceImageSession): Promise<void> {
    try {
      const image = await loadHtmlImage(overlay.objectUrl);

      if (!this.sys.isActive() || getReferenceImageSession() !== overlay) {
        return;
      }

      this.installReferenceTexture(image);
      this.spawnReferenceImage();
      this.rebuildHandles();
      this.syncMinimap();
    } catch {
      if (!this.sys.isActive() || getReferenceImageSession() !== overlay) {
        return;
      }

      this.disposeReferenceImage();
      clearReferenceImageSession();
      this.toolbar?.showMessage('Could not restore the reference image.', 'error');
      this.toolbar?.sync();
    }
  }

  private installReferenceTexture(image: HTMLImageElement): void {
    if (this.textures.exists(REFERENCE_IMAGE_TEXTURE_KEY)) {
      this.referenceImage?.destroy();
      this.referenceImage = null;
      this.textures.remove(REFERENCE_IMAGE_TEXTURE_KEY);
    }

    this.textures.addImage(REFERENCE_IMAGE_TEXTURE_KEY, image);
  }

  private spawnReferenceImage(): void {
    const overlay = getReferenceImageSession();

    if (!overlay || !this.textures.exists(REFERENCE_IMAGE_TEXTURE_KEY)) {
      return;
    }

    if (this.referenceImage && !this.referenceImage.scene) {
      this.referenceImage = null;
    }

    this.referenceImage ??= this.add.image(overlay.x, overlay.y, REFERENCE_IMAGE_TEXTURE_KEY);
    this.referenceImage.setOrigin(0.5);
    this.syncReferenceImage();
  }

  private syncReferenceImage(): void {
    const overlay = getReferenceImageSession();
    const image = this.referenceImage;

    if (!image || !image.scene) {
      this.syncReferenceAttr();
      return;
    }

    if (!overlay) {
      image.setVisible(false);
      this.syncReferenceAttr();
      return;
    }

    image.setPosition(overlay.x, overlay.y);
    image.setDisplaySize(overlay.width, overlay.height);
    image.setAlpha(this.referenceImageAlpha);
    image.setVisible(overlay.visible);
    image.setDepth(REFERENCE_DEPTH);
    this.syncReferenceAttr();
  }

  private syncReferenceAttr(): void {
    const overlay = getReferenceImageSession();
    const value = !overlay ? 'none' : overlay.visible ? 'on' : 'off';
    this.game.canvas.parentElement?.setAttribute('data-reference-image', value);
  }

  private disposeReferenceImage(): void {
    this.referenceImage?.destroy();
    this.referenceImage = null;

    if (this.textures.exists(REFERENCE_IMAGE_TEXTURE_KEY)) {
      this.textures.remove(REFERENCE_IMAGE_TEXTURE_KEY);
    }

    this.syncReferenceAttr();
  }

  private dragReference(point: Vec2): void {
    const drag = this.referenceDrag;

    if (!drag) {
      return;
    }

    const next =
      drag.kind === 'scale'
        ? scaleReferenceRect(drag.start, drag.origin, point, {
            minSize: REFERENCE_MIN_SIZE,
            snapStep: this.session.snapToGrid ? SNAP_SIZE_STEP : undefined,
          })
        : moveReferenceRect(
            drag.start,
            { x: point.x - drag.startPoint.x, y: point.y - drag.startPoint.y },
            this.session.snapToGrid ? SNAP_GRID : undefined,
          );

    patchReferenceImageSession(next);
    this.syncReferenceImage();
    this.syncReferenceHandles();
  }

  private setWaterlineHintAlpha(next: number): void {
    const alpha = clamp(next, 0, 1);

    if (alpha === this.map.look.waterlineHintAlpha) {
      return;
    }

    if (!this.lookEditing) {
      this.beginEdit();
      this.lookEditing = true;
    }

    updateLook(this.map.look, { waterlineHintAlpha: alpha });

    if (this.columnsVisible()) {
      this.redrawElevationLayers('chrome');
    }

    this.toolbar?.sync();
  }

  private applyAimChromeLive(patch: Partial<AimChrome>): void {
    if (!this.lookEditing) {
      this.beginEdit();
      this.lookEditing = true;
    }

    updateLook(this.map.look, {
      aimChrome: cloneAimChrome({
        movement: patch.movement ?? this.map.look.aimChrome.movement,
        prediction: patch.prediction ?? this.map.look.aimChrome.prediction,
        moveLine: patch.moveLine ?? this.map.look.aimChrome.moveLine,
        dest: patch.dest ?? this.map.look.aimChrome.dest,
      }),
    });
    this.toolbar?.sync();
  }

  /** Toolbar hook: the play-mode sun-column water sheet, off while authoring. */
  private setLightingPreview(on: boolean): void {
    if (on === this.previewLighting) {
      return;
    }

    this.previewLighting = on;

    if (!on && this.sunEditing) {
      this.commitSunEdit();
    }

    if (!on && this.lookEditing) {
      this.commitLookEdit();
    }

    this.syncLighting();
    this.persistEditorSettings();

    this.toolbar?.sync();
  }

  private redrawStartArea(): void {
    const gfx = this.startAreaGraphics;
    gfx.clear();

    const area = this.map.startArea;

    if (!area) {
      if (this.startAreaLabel?.scene) {
        this.startAreaLabel.destroy();
      }
      this.startAreaLabel = null;
      this.parkAreaLabel = null;
      return;
    }

    const selected = this.isRingSelected('startArea', '');
    const swatch = resolveColor(area.color);
    const widthPx = selected ? RING_SELECTED_STROKE_PX : RING_STROKE_PX;
    paintRing(
      gfx,
      area,
      swatch.fill,
      selected ? SELECTED_HIGHLIGHT : swatch.stroke,
      this.worldRadius(widthPx),
      0,
      swatch.alpha,
    );

    const center = ringCenter(area);

    if (this.startAreaLabel && this.startAreaLabel.scene == null) {
      this.startAreaLabel = null;
      this.parkAreaLabel = null;
    }

    if (!this.startAreaLabel) {
      this.startAreaLabel = this.add.text(0, 0, 'Start', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#eaffef',
      });
      this.startAreaLabel.setOrigin(0.5);
      this.startAreaLabel.setDepth(LABEL_DEPTH);
    }

    this.startAreaLabel.setPosition(center.x, center.y);
    this.startAreaLabel.setScale(this.worldRadius(1));
  }

  private redrawParkArea(): void {
    const gfx = this.parkAreaGraphics;
    gfx.clear();

    const area = this.map.parkArea;

    if (!area) {
      if (this.parkAreaLabel?.scene) {
        this.parkAreaLabel.destroy();
      }
      this.parkAreaLabel = null;
      return;
    }

    const selected = this.isRingSelected('parkArea', '');
    const swatch = resolveColor(area.color);
    const widthPx = selected ? RING_SELECTED_STROKE_PX : RING_STROKE_PX;
    paintRing(
      gfx,
      area,
      swatch.fill,
      selected ? SELECTED_HIGHLIGHT : swatch.stroke,
      this.worldRadius(widthPx),
      0,
      swatch.alpha,
    );

    const center = ringCenter(area);

    if (this.parkAreaLabel && this.parkAreaLabel.scene == null) {
      this.parkAreaLabel = null;
    }

    if (!this.parkAreaLabel) {
      this.parkAreaLabel = this.add.text(0, 0, 'Park', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#fff4d6',
      });
      this.parkAreaLabel.setOrigin(0.5);
      this.parkAreaLabel.setDepth(LABEL_DEPTH);
    }

    this.parkAreaLabel.setPosition(center.x, center.y);
    this.parkAreaLabel.setScale(this.worldRadius(1));
  }

  private flagHitSize(): { hitW: number; hitH: number } {
    const scale = this.poleScale();
    const radius = POLE_RADIUS_WORLD * scale;
    return {
      hitW: Math.max(radius, FLAG_RADIUS_WORLD) * 2,
      hitH: POLE_HEIGHT_WORLD * scale + radius,
    };
  }

  private applyFlagHitArea(handle: Phaser.GameObjects.Zone): void {
    const { hitW, hitH } = this.flagHitSize();
    handle.setSize(hitW, hitH);
    // Local (0, 0) is the origin (the waterline). The shaft sits in −Y, so the hit box
    // is a rectangle standing on that origin rather than Phaser's default (0, 0, w, h).
    handle.setInteractive(
      new Phaser.Geom.Rectangle(-hitW / 2, -hitH, hitW, hitH),
      Phaser.Geom.Rectangle.Contains,
    );
  }

  private createFlagHandle(checkpointId: string, flag: CheckpointFlag): Phaser.GameObjects.Zone {
    const { hitW, hitH } = this.flagHitSize();
    const handle = this.add.zone(0, 0, hitW, hitH);
    handle.setOrigin(0.5, 1);
    handle.setDepth(FLAG_DEPTH);
    handle.setData('kind', 'flag');
    handle.setData('checkpointId', checkpointId);
    handle.setData('flag', flag);
    this.applyFlagHitArea(handle);
    this.input.setDraggable(handle);
    handle.input!.cursor = hoverCursorForEditorTool(this.session.tool);
    return handle;
  }

  private syncFlag(checkpoint: Checkpoint, flag: CheckpointFlag): void {
    let flags = this.checkpointFlagHandles.get(checkpoint.id);

    if (flags && (flags.a.scene == null || flags.b.scene == null)) {
      this.checkpointFlagHandles.delete(checkpoint.id);
      flags = undefined;
    }

    if (!flags) {
      flags = { a: this.createFlagHandle(checkpoint.id, 'a'), b: this.createFlagHandle(checkpoint.id, 'b') };
      this.checkpointFlagHandles.set(checkpoint.id, flags);
    }

    flags[flag].setPosition(checkpoint[flag].x, checkpoint[flag].y);
    this.applyFlagHitArea(flags[flag]);
  }

  /**
   * Identity of everything `drawCheckpoints` reads. Play has `GatePaintClock` for the same
   * job; the editor has no race state to key on, so this is gate geometry plus the pole
   * selection and the live draft. Without it an island drag rebuilt all three gate layers
   * and the pole pool on every frame (docs/PERFORMANCE.md).
   */
  private checkpointPaintKey(
    strokeScale: number,
    selectedPoles: ReadonlyMap<string, CheckpointFlag[]>,
    draft: Vec2 | null,
  ): string {
    const parts = [String(strokeScale), String(this.poleScale())];

    for (const checkpoint of this.map.checkpoints) {
      // `order` is the badge number, so a renumber must repaint even though nothing moved.
      parts.push(
        `${checkpoint.id},${checkpoint.a.x},${checkpoint.a.y},${checkpoint.b.x},${checkpoint.b.y},${checkpoint.order},${checkpoint.isGoal ? 1 : 0},${(selectedPoles.get(checkpoint.id) ?? []).join('')}`,
      );
    }

    if (draft) {
      parts.push(`draft:${draft.x},${draft.y},${this.hover?.x ?? ''},${this.hover?.y ?? ''}`);
    }

    return parts.join('|');
  }

  private redrawCheckpoints(force = false): void {
    const strokeScale = this.worldRadius(1);
    const selectedPoles = new Map<string, CheckpointFlag[]>();

    for (const id of this.selectedCheckpointIds()) {
      selectedPoles.set(id, selectedCheckpointPoles(this.session.selection, id));
    }

    const draft = this.session.checkpointDraft;
    const key = this.checkpointPaintKey(strokeScale, selectedPoles, draft);

    if (!force && key === this.checkpointsDrawnFor) {
      return;
    }

    this.checkpointsDrawnFor = key;

    drawCheckpoints(
      {
        above: this.checkpointGraphics,
        waterline: this.checkpointWaterlineGraphics,
        submerged: this.checkpointSubmergedGraphics,
      },
      this.map,
      null,
      strokeScale,
      {
        selectedPoles,
        shafts: this.checkpointPoles ?? undefined,
        draft: draft && this.hover ? { a: draft, b: this.hover } : undefined,
      },
    );

    const seen = new Set<string>();

    for (const checkpoint of this.map.checkpoints) {
      seen.add(checkpoint.id);
      this.syncFlag(checkpoint, 'a');
      this.syncFlag(checkpoint, 'b');
    }

    syncCheckpointBadges(this, this.checkpointLabels, this.map, strokeScale, LABEL_DEPTH);

    for (const [id, flags] of this.checkpointFlagHandles) {
      if (!seen.has(id)) {
        flags.a.destroy();
        flags.b.destroy();
        this.checkpointFlagHandles.delete(id);
      }
    }
  }

  private redrawRoutes(): void {
    const gfx = this.routeGraphics;
    gfx.clear();

    this.syncShowRoutesFlag();

    if (!this.anyRouteVisible()) {
      this.syncRouteLabels();
      return;
    }

    const stroke = this.worldRadius(ROUTE_STROKE_PX);
    const selected = new Set(selectedIds(this.session.selection, 'route'));

    for (const route of this.map.routes) {
      if (this.hiddenRouteIds.has(route.id)) {
        continue;
      }
      const swatch = resolveColor(route.color);
      const active = selected.has(route.id);
      const width = active ? this.worldRadius(ROUTE_STROKE_PX + 1) : stroke;
      gfx.lineStyle(width, swatch.fill, active ? 1 : UNSELECTED_ROUTE_ALPHA);
      strokePolygon(gfx, routeOutline(route.points, route.cornerRadius), false);
    }

    this.syncRouteLabels();
  }

  private routeLabelColor(color: Rgba): string {
    return `#${rgb24(color).toString(16).padStart(6, '0')}`;
  }

  private syncRouteLabels(): void {
    const scale = this.worldRadius(1);
    const offset = this.worldRadius(16);
    const seen = new Set<string>();

    const place = (
      key: string,
      point: Vec2,
      text: string,
      color: Rgba,
      alpha: number,
    ): void => {
      seen.add(key);
      let label = this.routeLabels.get(key);

      if (label && label.scene == null) {
        this.routeLabels.delete(key);
        label = undefined;
      }

      if (!label) {
        label = this.add.text(0, 0, text, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: this.routeLabelColor(color),
        });
        label.setOrigin(0, 1);
        label.setStroke('#1b2a22', 3);
        label.setDepth(LABEL_DEPTH);
        this.routeLabels.set(key, label);
      }

      label.setText(text);
      label.setColor(this.routeLabelColor(color));
      label.setPosition(point.x + offset, point.y - offset);
      label.setScale(scale);
      label.setAlpha(alpha);
      label.setVisible(true);
    };

    if (!this.anyRouteVisible()) {
      for (const [key, label] of this.routeLabels) {
        if (label.scene) {
          label.destroy();
        }
        this.routeLabels.delete(key);
      }

      this.game.canvas.parentElement?.setAttribute('data-route-labels', '');
      return;
    }

    const selected = new Set(selectedIds(this.session.selection, 'route'));

    for (const route of this.map.routes) {
      if (this.hiddenRouteIds.has(route.id)) {
        continue;
      }

      const end = route.points[route.points.length - 1];

      if (!end) {
        continue;
      }

      place(
        `${route.id}:end`,
        end,
        formatRouteMetres(routeLengthM(route.points, route.cornerRadius)),
        route.color,
        selected.has(route.id) ? 1 : UNSELECTED_ROUTE_ALPHA,
      );
    }

    for (const [key, label] of this.routeLabels) {
      if (!seen.has(key) || label.scene == null) {
        if (label.scene) {
          label.destroy();
        }
        this.routeLabels.delete(key);
      }
    }

    const texts: string[] = [];

    for (const label of this.routeLabels.values()) {
      if (label.scene && label.visible && label.text !== '') {
        texts.push(label.text);
      }
    }

    this.game.canvas.parentElement?.setAttribute('data-route-labels', texts.join(','));
  }

  private redrawDraft(): void {
    const gfx = this.draftGraphics;
    gfx.clear();

    const points = this.session.draft;

    if (points.length === 0) {
      this.syncDraftRouteLabels();
      return;
    }

    const preview = this.hover ? [...points, this.hover] : points;
    const isRoute = this.session.tool === 'draw-route';
    const color = isRoute ? nextRouteColor(this.map) : null;
    const stroke = color ? resolveColor(color).fill : DRAFT_STROKE;
    gfx.lineStyle(this.worldRadius(DRAFT_STROKE_PX), stroke, 1);
    strokePolygon(gfx, preview, false);
    gfx.fillStyle(stroke, 1);

    for (const point of points) {
      gfx.fillCircle(point.x, point.y, this.worldRadius(HANDLE_RADIUS_PX));
    }

    this.syncDraftRouteLabels();
  }

  /** Metre labels appear only after Enter, on the committed curve. */
  private syncDraftRouteLabels(): void {
    const draftKeys = [...this.routeLabels.keys()].filter((key) => key.startsWith('draft:'));

    for (const key of draftKeys) {
      const label = this.routeLabels.get(key);

      if (label?.scene) {
        label.destroy();
      }

      this.routeLabels.delete(key);
    }
  }

  /**
   * Vertex knobs, bend dots, and the rotate/scale gizmos hide once the selection is actually
   * translating. A click that never moves still shows them; handle drags keep their own chrome.
   */
  private selectionHandlesHidden(): boolean {
    return this.groupDrag?.moved === true || !ringSculptingEnabled(this.showElevation);
  }

  private syncSelectionHandleAttr(): void {
    this.game.canvas.parentElement?.setAttribute(
      'data-selection-handles',
      this.selectionHandlesHidden() ? 'hidden' : 'shown',
    );
  }

  private currentGizmoMode(): SelectionGizmoMode {
    return selectionGizmoMode(
      this.session.tool,
      this.session.selection.refs.length,
      this.showElevation,
    );
  }

  private syncSelectionGizmoAttr(): void {
    const mode = this.selectionHandlesHidden() ? 'none' : this.currentGizmoMode();
    this.game.canvas.parentElement?.setAttribute('data-selection-gizmo', mode);
  }

  private rebuildHandles(): void {
    for (const handle of this.handles) {
      handle.destroy();
    }

    this.handles = [];
    this.buildReferenceHandles();

    if (!isSelectionTool(this.session.tool) || this.selectionHandlesHidden()) {
      this.syncSelectionHandleAttr();
      this.syncSelectionGizmoAttr();
      return;
    }

    this.buildSelectionGizmos();

    if (this.session.tool !== 'select') {
      this.syncSelectionHandleAttr();
      this.syncSelectionGizmoAttr();
      return;
    }

    const routeRef = soleRef(this.session.selection);

    if (routeRef?.kind === 'route') {
      if (this.hiddenRouteIds.has(routeRef.id)) {
        this.syncSelectionHandleAttr();
        this.syncSelectionGizmoAttr();
        return;
      }

      const route = findRoute(this.map, routeRef.id);
      const radius = this.worldRadius(HANDLE_RADIUS_PX);

      route?.points.forEach((vertex, index) => {
        const handle = this.add.circle(vertex.x, vertex.y, radius, VERTEX_HANDLE_FILL);
        handle.setDepth(HANDLE_DEPTH);
        handle.setStrokeStyle(2, VERTEX_HANDLE_STROKE);
        handle.setData('kind', 'route-vertex');
        handle.setData('ownerId', route.id);
        handle.setData('index', index);
        handle.setInteractive({ draggable: true, useHandCursor: false });
        this.input.setDraggable(handle);
        handle.input!.cursor = hoverCursorForEditorTool(this.session.tool);
        this.handles.push(handle);
      });

      this.syncSelectionHandleAttr();
      this.syncSelectionGizmoAttr();
      return;
    }

    const owner = this.ringOwnerFromSelection();

    if (!owner) {
      this.syncSelectionHandleAttr();
      this.syncSelectionGizmoAttr();
      return;
    }

    const ring = this.getRingFor(owner);

    if (!ring) {
      this.syncSelectionHandleAttr();
      this.syncSelectionGizmoAttr();
      return;
    }

    const radius = this.worldRadius(HANDLE_RADIUS_PX);
    const bendRadius = this.worldRadius(BEND_HANDLE_RADIUS_PX);

    ring.vertices.forEach((vertex, index) => {
      const handle = this.add.circle(vertex.x, vertex.y, radius, VERTEX_HANDLE_FILL);
      handle.setDepth(HANDLE_DEPTH);
      handle.setStrokeStyle(2, VERTEX_HANDLE_STROKE);
      handle.setData('kind', 'vertex');
      handle.setData('ownerKind', owner.kind);
      handle.setData('ownerId', owner.id);
      handle.setData('index', index);
      handle.setInteractive({ draggable: true, useHandCursor: false });
      this.input.setDraggable(handle);
      handle.input!.cursor = hoverCursorForEditorTool(this.session.tool);
      this.handles.push(handle);
    });

    ring.edges.forEach((edge, edgeIndex) => {
      if (!edge.bend) {
        return;
      }

      const handle = this.add.circle(edge.bend.x, edge.bend.y, bendRadius, BEND_HANDLE_FILL);
      handle.setDepth(HANDLE_DEPTH);
      handle.setStrokeStyle(2, BEND_HANDLE_STROKE);
      handle.setData('kind', 'bend');
      handle.setData('ownerKind', owner.kind);
      handle.setData('ownerId', owner.id);
      handle.setData('edgeIndex', edgeIndex);
      handle.setInteractive({ draggable: true, useHandCursor: false });
      this.input.setDraggable(handle);
      handle.input!.cursor = hoverCursorForEditorTool(this.session.tool);
      this.handles.push(handle);
    });

    this.syncSelectionHandleAttr();
    this.syncSelectionGizmoAttr();
  }

  private selectionFrameRect(): Rect | null {
    if (this.session.selection.refs.length < 1) {
      return null;
    }

    const bounds = selectionBounds(this.map, this.session.selection.refs);

    if (!bounds) {
      return null;
    }

    const pad = this.worldRadius(SELECTION_BOX_PAD_PX);
    return {
      minX: bounds.minX - pad,
      minY: bounds.minY - pad,
      maxX: bounds.maxX + pad,
      maxY: bounds.maxY + pad,
    };
  }

  private groupScaleRect(): Rect | null {
    return gizmoShowsScale(this.currentGizmoMode()) ? this.selectionFrameRect() : null;
  }

  private groupHandleCorner(gameObject: Phaser.GameObjects.GameObject): GizmoCorner | null {
    const x = gameObject.getData('cornerX');
    const y = gameObject.getData('cornerY');
    return x === -1 || x === 1 ? (y === -1 || y === 1 ? { x, y } : null) : null;
  }

  private referenceHandlesEnabled(): boolean {
    const overlay = getReferenceImageSession();
    return (
      overlay !== null &&
      overlay.visible &&
      (this.session.tool === 'select' || this.session.tool === 'scale')
    );
  }

  private buildReferenceHandles(): void {
    if (!this.referenceHandlesEnabled()) {
      return;
    }

    const overlay = getReferenceImageSession();

    if (!overlay) {
      return;
    }

    const frame = referenceImageFrame(overlay);
    const radius = this.worldRadius(HANDLE_RADIUS_PX);

    for (const corner of GIZMO_CORNERS) {
      const point = gizmoCornerPoint(frame, corner);
      const handle = this.add.circle(point.x, point.y, radius, RESIZE_HANDLE_FILL);
      handle.setDepth(HANDLE_DEPTH);
      handle.setStrokeStyle(2, VERTEX_HANDLE_STROKE);
      handle.setData('kind', 'reference-resize');
      handle.setData('cornerX', corner.x);
      handle.setData('cornerY', corner.y);
      handle.setInteractive({ draggable: true, useHandCursor: false });
      this.input.setDraggable(handle);
      handle.input!.cursor = hoverCursorForEditorTool(this.session.tool);
      this.handles.push(handle);
    }

    const move = this.add.circle(overlay.x, overlay.y, radius, VERTEX_HANDLE_FILL);
    move.setDepth(HANDLE_DEPTH);
    move.setStrokeStyle(2, VERTEX_HANDLE_STROKE);
    move.setData('kind', 'reference-move');
    move.setInteractive({ draggable: true, useHandCursor: false });
    this.input.setDraggable(move);
    move.input!.cursor = hoverCursorForEditorTool(this.session.tool);
    this.handles.push(move);
  }

  private syncReferenceHandles(): void {
    const overlay = getReferenceImageSession();

    if (!overlay) {
      return;
    }

    const frame = referenceImageFrame(overlay);

    for (const handle of this.handles) {
      const kind = handle.getData('kind');

      if (kind === 'reference-move') {
        handle.setPosition(overlay.x, overlay.y);
        continue;
      }

      if (kind !== 'reference-resize') {
        continue;
      }

      const corner = this.groupHandleCorner(handle);

      if (corner) {
        const point = gizmoCornerPoint(frame, corner);
        handle.setPosition(point.x, point.y);
      }
    }
  }

  private buildSelectionGizmos(): void {
    const mode = this.currentGizmoMode();
    const frame = this.selectionFrameRect();

    if (!frame || mode === 'none') {
      return;
    }

    const radius = this.worldRadius(HANDLE_RADIUS_PX);
    const offset = this.worldRadius(ROTATE_HANDLE_OFFSET_PX);

    for (const corner of GIZMO_CORNERS) {
      if (gizmoShowsScale(mode)) {
        const point = gizmoCornerPoint(frame, corner);
        const handle = this.add.circle(point.x, point.y, radius, RESIZE_HANDLE_FILL);
        handle.setDepth(HANDLE_DEPTH);
        handle.setStrokeStyle(2, VERTEX_HANDLE_STROKE);
        handle.setData('kind', 'group-resize');
        handle.setData('cornerX', corner.x);
        handle.setData('cornerY', corner.y);
        handle.setInteractive({ draggable: true, useHandCursor: false });
        this.input.setDraggable(handle);
        handle.input!.cursor = hoverCursorForEditorTool(this.session.tool);
        this.handles.push(handle);
      }

      if (gizmoShowsRotate(mode)) {
        const point = gizmoRotateHandlePoint(frame, corner, offset);
        const handle = this.add.circle(point.x, point.y, radius, ROTATE_HANDLE_FILL);
        handle.setDepth(HANDLE_DEPTH);
        handle.setStrokeStyle(2, VERTEX_HANDLE_STROKE);
        handle.setData('kind', 'group-rotate');
        handle.setData('cornerX', corner.x);
        handle.setData('cornerY', corner.y);
        handle.setInteractive({ draggable: true, useHandCursor: false });
        this.input.setDraggable(handle);
        handle.input!.cursor = hoverCursorForEditorTool(this.session.tool);
        this.handles.push(handle);
      }
    }
  }

  /**
   * Move the handles that already exist to their current anchors. A drag changes where a
   * handle sits, not which handles there are, so this replaces `rebuildHandles` on the live
   * path — that one destroys and re-registers an interactive Arc per vertex and bend.
   *
   * A group drag hides the knobs until release. `rebuildHandles` does that by creating none;
   * here it is `setVisible`, which Phaser also takes out of the input hit test. The commit
   * at the end of the gesture rebuilds them for real.
   */
  private syncHandlePositions(): void {
    if (this.handles.length === 0) {
      return;
    }

    if (this.selectionHandlesHidden()) {
      for (const handle of this.handles) {
        handle.setVisible(false);
      }

      return;
    }

    for (const handle of this.handles) {
      handle.setVisible(true);
    }

    const frame = this.selectionFrameRect();
    const scaleRect = this.groupScaleRect();
    const rotateOffset = this.worldRadius(ROTATE_HANDLE_OFFSET_PX);

    for (const handle of this.handles) {
      const kind = handle.getData('kind');

      if (kind === 'group-resize' && scaleRect) {
        const corner = this.groupHandleCorner(handle);

        if (corner) {
          const point = gizmoCornerPoint(scaleRect, corner);
          handle.setPosition(point.x, point.y);
        }

        continue;
      }

      if (kind === 'group-rotate') {
        const corner = frame ? this.groupHandleCorner(handle) : null;

        if (corner && frame) {
          const point = gizmoRotateHandlePoint(frame, corner, rotateOffset);
          handle.setPosition(point.x, point.y);
        }

        continue;
      }

      if (kind === 'route-vertex') {
        const routeId = handle.getData('ownerId') as string;
        const index = handle.getData('index') as number;
        const vertex = findRoute(this.map, routeId)?.points[index];

        if (vertex) {
          handle.setPosition(vertex.x, vertex.y);
        }

        continue;
      }

      if (kind === 'vertex') {
        const owner: RingOwner = {
          kind: handle.getData('ownerKind') as RingOwnerKind,
          id: handle.getData('ownerId') as string,
        };
        const index = handle.getData('index') as number;
        const vertex = this.getRingFor(owner)?.vertices[index];

        if (vertex) {
          handle.setPosition(vertex.x, vertex.y);
        }

        continue;
      }

      if (kind === 'bend') {
        const owner: RingOwner = {
          kind: handle.getData('ownerKind') as RingOwnerKind,
          id: handle.getData('ownerId') as string,
        };
        const edgeIndex = handle.getData('edgeIndex') as number;
        const bend = this.getRingFor(owner)?.edges[edgeIndex]?.bend;

        if (bend) {
          handle.setPosition(bend.x, bend.y);
        }
      }
    }
  }

  private dragGroupScale(
    point: Vec2,
    gameObject: Phaser.GameObjects.GameObject,
    uniform: boolean,
  ): void {
    if (!this.groupScale) {
      return;
    }

    const rect = this.groupScaleRect();
    const corner = this.groupHandleCorner(gameObject);

    if (!rect || !corner) {
      return;
    }

    const handle = gizmoCornerPoint(rect, corner);
    const origin = this.groupScale.origin;

    if (uniform) {
      const startLen = length(sub(handle, origin));
      const nowLen = length(sub(point, origin));

      if (startLen < 1e-3) {
        return;
      }

      let factor = nowLen / startLen;

      if (this.session.snapToGrid) {
        factor = snapValue(factor, SNAP_SIZE_STEP);
      }

      if (factor === 1) {
        return;
      }

      scaleSelection(this.session, this.map, origin, factor, factor, { uniform: true });
    } else {
      const dx = handle.x - origin.x;
      const dy = handle.y - origin.y;
      let scaleX = Math.abs(dx) < 1e-6 ? 1 : (point.x - origin.x) / dx;
      let scaleY = Math.abs(dy) < 1e-6 ? 1 : (point.y - origin.y) / dy;

      if (this.session.snapToGrid) {
        scaleX = snapValue(scaleX, SNAP_SIZE_STEP);
        scaleY = snapValue(scaleY, SNAP_SIZE_STEP);
      }

      if (scaleX === 1 && scaleY === 1) {
        return;
      }

      scaleSelection(this.session, this.map, origin, scaleX, scaleY);
    }

    this.requestDragPaint();
  }

  private dragGroupRotate(point: Vec2): void {
    if (!this.groupRotate) {
      return;
    }

    const { origin, startAngle } = this.groupRotate;
    let target = angleDelta(startAngle, toAngle(sub(point, origin)));

    if (this.session.snapToGrid) {
      target = snapAngleRad(target);
    }

    const delta = target - this.groupRotate.appliedRad;

    if (Math.abs(delta) < 1e-9) {
      return;
    }

    this.groupRotate.appliedRad += rotateSelection(this.session, this.map, origin, delta);
    this.requestDragPaint();
  }
}
